import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, Link } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useQueryClient } from '@tanstack/react-query';
import {
  useAllQuotes,
  useQuote,
  useQuoteHistory,
  useQuotePriceHistory,
  useQuoteDiagnosticsHistory,
  useCompetitorSignals,
  useEntriesDiagnosticsHistory,
  useQuoteLines,
  useQuoteLadder,
  useQuoteUpdates,
  useQuotesByFixture,
  useMarketEntries,
  useFilterLeagues,
  useFilterMarketIds,
  useFilterTeams,
  useFilterPlayers,
  useConfig,
  useMasterActivityLog,
  useEntryStatsByLine,
  useDerivedModelDetail,
  useModelMarketRules,
} from '../api/hooks';
import ManualGatingPanel from '../components/ManualGatingPanel';
import GateBreakdownPanel from '../components/GateBreakdownPanel';
import UncertaintyBreakdownPanel from '../components/UncertaintyBreakdownPanel';
import { sportBucketForFixture } from '../utils/batchSuspensionUtils';
import OrderBookTerminal from '../components/OrderBookTerminal';
import OrderBookClearLog from '../components/OrderBookClearLog';
import DerivedModelBreakdownPanel from '../components/DerivedModelBreakdownPanel';
import {
  enrichQuoteHistoryWithModelOutputs,
  findApplicableModelRule,
  isBooklessDerivedMarket,
  isBooklessModelMarket,
  marketIdFromMarketKey,
  parseModelOutputTimestamp,
} from '../utils/booklessDerivedMarket';
import { dateFromInputValue, marketLabel } from './TradingDash/tradingDashUtils';
import { useMarketDashboard } from './MarketDashboard/useMarketDashboard';
import LiabilityTab from './MarketDashboard/marketDashboardLiability';
import { getQuoteGateDisplay, rotowireLineupLabel } from '../utils/quoteGateDisplay';
import { entryPromoRowClass, formatReplicationBadge, sharpCellStyle } from './entryTickerUtils';
import './EntriesPage.css';
import './DashboardPage.css';
import './MarketDashboard/MarketDashboard.css';

/** Fair probability line color (True Probability + CI, Signals & entries). */
const FAIR_PROB_LINE_COLOR = '#B45309';

const COMPETITOR_EVENT_COLORS = {
  discovery: '#22C55E',
  jump: '#F59E0B',
  jump_decay: '#34D399',
  vig_only: '#94A3B8',
  vig_tighten: '#38BDF8',
  dropout: '#EF4444',
  alt_flick_sup: '#F472B6',
  alt_flicker_suppression: '#F472B6',
  baseline: '#64748B',
  baseline_shift_reseed: '#818CF8',
  seed: '#C084FC',
  no_move: '#64748B',
  discovery_suppressed: '#86EFAC',
  line_change: '#38BDF8',
  line_flicker_revert: '#22D3EE',
  dropout_feed_resume: '#94A3B8',
  ob_clear: '#A78BFA',
  ob_virtual_clear: '#C4B5FD',
  ob_partial_depth: '#64748B',
  tob_cap: '#FB7185',
  tob_exhaust: '#C084FC',
  tob_clear: '#34D399',
  entries_fuse: '#38BDF8',
  panel: '#64748B',
  discovery_convergence_suppressed: '#86EFAC',
  price_flicker_revert: '#22D3EE',
  d_sup: '#86EFAC',
  d_conv_sup: '#86EFAC',
  d_out_sup: '#86EFAC',
  discovery_outlier_suppressed: '#86EFAC',
  do_resume: '#94A3B8',
  bl_reseed: '#818CF8',
  lfr: '#22D3EE',
  pfr: '#22D3EE',
};

const CE_SHORT_LABELS = {
  discovery: 'Discovery pull',
  d_sup: 'Discovery suppressed (z² gate)',
  discovery_suppressed: 'Discovery suppressed (z² gate)',
  d_conv_sup: 'Discovery suppressed (converging)',
  discovery_convergence_suppressed: 'Discovery suppressed (converging)',
  d_out_sup: 'Discovery suppressed (outlier vs pack)',
  discovery_outlier_suppressed: 'Discovery suppressed (outlier vs pack)',
  jump: 'Jump — P inflated',
  jump_decay: 'Jump decay — P unwound',
  vig_only: 'Vig widened',
  vig_tighten: 'Vig tightened',
  no_move: 'No fair move',
  dropout: 'Book dropout',
  alt_flick_sup: 'Alt flicker suppression',
  alt_flicker_suppression: 'Alt flicker suppression',
  do_resume: 'Dropout rejoin',
  dropout_feed_resume: 'Dropout rejoin',
  baseline: 'First book quote',
  entries_fuse: 'Comp price update',
  panel: 'Panel (fuse tick)',
  seed: 'Opening seed',
  bl_reseed: 'Base line reseed',
  baseline_shift_reseed: 'Base line reseed',
  lfr: 'Line flicker revert',
  line_flicker_revert: 'Line flicker revert',
  pfr: 'Price flicker revert',
  price_flicker_revert: 'Price flicker revert',
  ob_clear: 'Order book clear',
  ob_virtual_clear: 'Virtual OB clear',
  ob_partial_depth: 'Partial OB depth',
  tob_cap: 'TOB risk · capping',
  tob_exhaust: 'TOB risk · exhausted',
  tob_clear: 'TOB risk · cleared',
};

function competitorEventInnerLabel(inner) {
  return CE_SHORT_LABELS[inner] || inner.replace(/_/g, ' ');
}

/** One-line label for chart tooltips / table hover (not backend signal_definition). */
function shortCompetitorEventDescription(event) {
  const e = String(event || '');
  if (!e) return '';
  if (CE_SHORT_LABELS[e]) return CE_SHORT_LABELS[e];
  if (e.startsWith('pfr_')) {
    return `Price flicker · ${competitorEventInnerLabel(e.slice(4))}`;
  }
  if (e.startsWith('price_flicker_revert_')) {
    return `Price flicker · ${competitorEventInnerLabel(e.slice('price_flicker_revert_'.length))}`;
  }
  if (e.startsWith('lfr_')) {
    return `Line flicker · ${competitorEventInnerLabel(e.slice(4))}`;
  }
  if (e.startsWith('line_flicker_revert_')) {
    return `Line flicker · ${competitorEventInnerLabel(e.slice('line_flicker_revert_'.length))}`;
  }
  if (e.startsWith('lc_')) {
    return `Line change · ${competitorEventInnerLabel(e.slice(3))}`;
  }
  if (e.startsWith('line_change_')) {
    return `Line change · ${competitorEventInnerLabel(e.slice('line_change_'.length))}`;
  }
  return e.replace(/_/g, ' ');
}

/** Bid/ask cell: decimal odds + that leg's implied prob (1/decimal). */
function formatSpreadLegCell(price) {
  if (price == null || !Number.isFinite(Number(price)) || Number(price) <= 0) return '—';
  const implied = 1 / Number(price);
  const pct = Math.min(100, Math.max(0, implied * 100));
  return (
    <span
      className="spread-price-cell"
      title={`Implied ${(pct).toFixed(2)}% = 1/${Number(price).toFixed(3)} (leg; two-sided legs sum to overround, not 100%)`}
    >
      <strong className="spread-price-dec">{Number(price).toFixed(2)}</strong>
      <span className="spread-price-pct">{` (${pct.toFixed(1)}%)`}</span>
    </span>
  );
}

function lineMatches(a, b, eps = 0.01) {
  if (a == null || b == null) return false;
  const x = Number(a);
  const y = Number(b);
  return Number.isFinite(x) && Number.isFinite(y) && Math.abs(x - y) < eps;
}

// Competitor panel order: the books worth reading first, then the rest alphabetically.
// Matched on a lowercased prefix of the Optic display name, which is what price history
// carries ('DraftKings', 'Kalshi') — not the normalized book key.
const BOOK_PANEL_ORDER = ['draftkings', 'fanduel', 'underdog', 'prizepicks', 'kalshi', 'novig'];

// Exchange depth. Number(null) is 0, so null must be rejected before the finite check or
// every book that publishes no order_book reports zero liquidity.
function nullableSize(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

// null = book publishes no order_book, so render nothing rather than 0.
function fmtLiq(v) {
  if (nullableSize(v) == null) return null;
  const n = Number(v);
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return String(Math.round(n));
}

function bookPanelRank(displayName) {
  const n = String(displayName || '').trim().toLowerCase();
  const i = BOOK_PANEL_ORDER.findIndex((p) => n.startsWith(p));
  return i === -1 ? BOOK_PANEL_ORDER.length : i;
}

function compareBooksForPanel(a, b) {
  return bookPanelRank(a) - bookPanelRank(b) || String(a).localeCompare(String(b));
}

/** Sleeper Odds row for competitor pricing scrub (live quote or historical ladder/prices). */
function buildSleeperSpreadRow({
  atLatest,
  currentQuote,
  selectedLine,
  point,
  pointTs,
  quoteHistory,
  ladderRows,
  ourPFair,
}) {
  const chartLine = Number.isFinite(Number(selectedLine)) ? Number(selectedLine) : null;

  if (atLatest && currentQuote) {
    const liveOver =
      currentQuote.marginated_price != null && currentQuote.marginated_price > 0
        ? Number(currentQuote.marginated_price)
        : null;
    const gatedOver =
      currentQuote.marginated_price_if_gated != null && currentQuote.marginated_price_if_gated > 0
        ? Number(currentQuote.marginated_price_if_gated)
        : null;
    const liveUnder =
      currentQuote.marginated_price_under != null && currentQuote.marginated_price_under > 0
        ? Number(currentQuote.marginated_price_under)
        : null;
    const gatedUnder =
      currentQuote.marginated_price_under_if_gated != null
      && currentQuote.marginated_price_under_if_gated > 0
        ? Number(currentQuote.marginated_price_under_if_gated)
        : null;
    const ourOver = liveOver ?? gatedOver;
    const ourUnder = liveUnder ?? gatedUnder;
    const askQuoted = liveOver != null;
    const bidQuoted = liveUnder != null;
    const ourAsk = isChartablePrice(ourOver) ? 1 / ourOver : null;
    const ourBid = isChartablePrice(ourUnder) ? 1 - 1 / ourUnder : null;
    const ourLine = currentQuote.line != null && Number.isFinite(Number(currentQuote.line))
      ? Number(currentQuote.line)
      : chartLine;
    if (ourAsk == null && ourBid == null && ourPFair == null) return null;
    return {
      book: 'Sleeper Odds',
      ask: ourAsk,
      bid: ourBid,
      bookFair: ourPFair,
      priceOver: isChartablePrice(ourOver) ? ourOver : null,
      priceUnder: isChartablePrice(ourUnder) ? ourUnder : null,
      twoSided: askQuoted && bidQuoted,
      isOurs: true,
      sourceLine: ourLine,
      translated: false,
      bidQuoted,
      askQuoted,
    };
  }

  if (pointTs == null || !Number.isFinite(pointTs)) return null;

  // Historical: nearest ladder row at the chart line (at-or-before preferred).
  let ladderHit = null;
  let bestDt = Infinity;
  let bestPref = Infinity;
  for (const row of ladderRows || []) {
    const ts = row?.timestamp != null ? Number(row.timestamp) : null;
    if (ts == null || !Number.isFinite(ts)) continue;
    const rowLine = row.line != null ? Number(row.line) : Number(row.base_line);
    if (chartLine != null && !lineMatches(rowLine, chartLine)) continue;
    const dt = Math.abs(ts - pointTs);
    const pref = ts <= pointTs + 1e-6 ? dt : dt + 1e6;
    if (pref < bestPref || (pref === bestPref && dt < bestDt)) {
      bestPref = pref;
      bestDt = dt;
      ladderHit = row;
    }
  }
  if (ladderHit != null && bestDt > 300) ladderHit = null;

  let ourOver = ladderHit?.marginated_price != null && Number(ladderHit.marginated_price) > 0
    ? Number(ladderHit.marginated_price)
    : null;
  let ourUnder = ladderHit?.marginated_price_under != null && Number(ladderHit.marginated_price_under) > 0
    ? Number(ladderHit.marginated_price_under)
    : null;
  if (ourOver == null && isChartablePrice(point?.marginated_price)) {
    ourOver = Number(point.marginated_price);
  }

  let fair = ourPFair;
  if (fair == null && ladderHit?.p_fair_base != null && Number.isFinite(Number(ladderHit.p_fair_base))) {
    fair = Number(ladderHit.p_fair_base);
  }
  if (fair == null && ladderHit?.p_over != null && Number.isFinite(Number(ladderHit.p_over))) {
    fair = Number(ladderHit.p_over);
  }
  if (fair == null && quoteHistory?.length) {
    let best = null;
    let qBestDt = Infinity;
    for (const q of quoteHistory) {
      const qts = q.timestamp != null ? Number(q.timestamp) : (q.time != null ? Number(q.time) / 1000 : null);
      if (qts == null || q.p_fair == null || !Number.isFinite(Number(q.p_fair))) continue;
      const dt = Math.abs(qts - pointTs);
      if (dt < qBestDt) {
        qBestDt = dt;
        best = Number(q.p_fair);
      }
    }
    if (best != null && qBestDt <= 120) fair = best;
  }

  const ourAsk = isChartablePrice(ourOver) ? 1 / ourOver : null;
  const ourBid = isChartablePrice(ourUnder) ? 1 - 1 / ourUnder : null;
  if (ourAsk == null && ourBid == null && fair == null) return null;
  const sourceLine = ladderHit?.line != null && Number.isFinite(Number(ladderHit.line))
    ? Number(ladderHit.line)
    : chartLine;
  return {
    book: 'Sleeper Odds',
    ask: ourAsk,
    bid: ourBid,
    bookFair: fair,
    priceOver: isChartablePrice(ourOver) ? ourOver : null,
    priceUnder: isChartablePrice(ourUnder) ? ourUnder : null,
    twoSided: ourAsk != null && ourBid != null,
    isOurs: true,
    sourceLine,
    translated: false,
    // Historical ladder/prices are what we were quoting at that time.
    bidQuoted: ourUnder != null,
    askQuoted: ourOver != null,
  };
}

function formatCompetitorTime(tsSec) {
  if (tsSec == null || !Number.isFinite(Number(tsSec))) return '—';
  const d = new Date(Number(tsSec) * 1000);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatEntriesPMarket(row) {
  const p = row.kf_p_market_end ?? row.kf_p_after;
  if (p == null) return '—';
  const main = `${(Number(p) * 100).toFixed(2)}%`;
  const before = row.kf_p_before;
  const after = row.kf_p_after;
  if (
    before != null
    && after != null
    && Math.abs(Number(before) - Number(after)) > 1e-6
  ) {
    const delta = (Number(after) - Number(before)) * 100;
    const sign = delta >= 0 ? '+' : '';
    return (
      <span className="competitor-val-stack" title="Entries posterior after batch (Δ vs start of batch)">
        <span className="competitor-val-new">{main}</span>
        <span className="competitor-val-old">{sign}{delta.toFixed(2)}pp</span>
      </span>
    );
  }
  return <span title="Entries posterior after batch">{main}</span>;
}

function formatCompetitorDeltaP(row) {
  const before = row.kf_P_before;
  const after = row.kf_P_after;
  if (before == null || after == null) {
    return row.kf_P_after != null ? Number(row.kf_P_after).toFixed(3) : '—';
  }
  const dP = Number(after) - Number(before);
  const afterStr = Number(after).toFixed(3);
  if (Math.abs(dP) < 1e-6) return afterStr;
  return (
    <span className="competitor-val-stack" title={`P ${Number(before).toFixed(4)} → ${Number(after).toFixed(4)}`}>
      <span className="competitor-val-new">{afterStr}</span>
      <span style={{ color: dP > 0 ? '#34d399' : '#fb7185' }}>
        {dP > 0 ? '+' : '−'}{Math.abs(dP).toFixed(3)}
      </span>
    </span>
  );
}

function shortBookLabel(name) {
  const s = String(name || '').trim();
  if (!s) return '?';
  const known = {
    draftkings: 'DK',
    fanduel: 'FD',
    'william hill': 'WH',
    caesars: 'CZR',
    betmgm: 'MGM',
    bovada: 'BOV',
    prizepicks: 'PP',
    betano: 'BTN',
    thescore: 'SCR',
    'hard rock': 'HR',
    'underdog fantasy (multipliers)': 'UD',
    underdog: 'UD',
    unibet: 'UNI',
    betway: 'BW',
  };
  const hit = known[s.toLowerCase()];
  if (hit) return hit;
  return s.length <= 10 ? s : `${s.slice(0, 8)}…`;
}

function competitorBookDetail(row) {
  const lineFrom = row.source_line_from;
  const lineTo = row.source_line_to ?? row.source_line_from;
  const overFrom = row.decimal_over_from ?? row.price_over_from;
  const overTo = row.decimal_over_to ?? row.price_over_to ?? overFrom;
  const underFrom = row.decimal_under_from ?? row.price_under_from;
  const underTo = row.decimal_under_to ?? row.price_under_to ?? underFrom;
  const fmtPair = (o, u) => {
    if (o == null || !Number.isFinite(Number(o))) return null;
    const os = Number(o).toFixed(2);
    return u != null && Number.isFinite(Number(u)) ? `${os}/${Number(u).toFixed(2)}` : os;
  };
  const moved = (a, b, tol = 1e-3) => {
    if (a == null || b == null) return a != null || b != null;
    return Math.abs(Number(a) - Number(b)) > tol;
  };
  const parts = [];
  if (moved(lineFrom, lineTo, 0.01) && lineFrom != null && lineTo != null) {
    parts.push(`${Number(lineFrom).toFixed(1)}→${Number(lineTo).toFixed(1)}`);
  } else if (lineTo != null && Number.isFinite(Number(lineTo))) {
    parts.push(Number(lineTo).toFixed(1));
  }
  const fromPx = fmtPair(overFrom, underFrom);
  const toPx = fmtPair(overTo, underTo);
  if (fromPx && toPx && (moved(overFrom, overTo) || moved(underFrom, underTo))) {
    parts.push(`${fromPx}→${toPx}`);
  } else if (toPx) {
    parts.push(toPx);
  }
  return parts.join(' ');
}

/** Collapse one ingest batch (fuse or per-book) into a single table row. */
function summarizeCompetitorSignals(signals) {
  const rows = Array.isArray(signals) ? signals : [];
  const groups = new Map();
  const order = [];

  const batchKey = (row) => {
    const ts = Number(row?.timestamp);
    if (!Number.isFinite(ts)) return null;
    const bs = Number(row?.batch_size);
    if (Number.isFinite(bs) && bs > 1 && row?.batch_index != null) {
      return `b:${ts.toFixed(3)}:${bs}`;
    }
    const ev = String(row?.event || '');
    if (ev === 'entries_fuse' || ev === 'seed' || ev === 'panel') {
      return `f:${ts.toFixed(3)}:${ev}`;
    }
    return null;
  };

  for (const row of rows) {
    const key = batchKey(row) || `s:${row?.id ?? `${row?.timestamp}:${row?.sportsbook}:${row?.event}`}`;
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key).push(row);
  }

  return order.map((key) => {
    const members = groups.get(key) || [];
    members.sort((a, b) => Number(a.batch_index ?? 0) - Number(b.batch_index ?? 0));
    const head = members[0] || {};
    const events = [...new Set(members.map((m) => shortCompetitorEventDescription(m.event)).filter(Boolean))];
    const books = members.map((m) => ({
      name: m.sportsbook,
      short: shortBookLabel(m.sportsbook),
      detail: competitorBookDetail(m),
      event: shortCompetitorEventDescription(m.event),
    }));
    const deltas = members.map((m) => m.delta_mid).filter((v) => v != null && Number.isFinite(Number(v)));
    const sameDelta = deltas.length > 0 && deltas.every((v) => Math.abs(Number(v) - Number(deltas[0])) < 1e-9);
    const rs = members.map((m) => m.R_obs).filter((v) => v != null && Number.isFinite(Number(v)));
    const sameR = rs.length > 0 && rs.every((v) => Math.abs(Number(v) - Number(rs[0])) < 1e-12);
    const ks = members.map((m) => m.K).filter((v) => v != null && Number.isFinite(Number(v)));
    const sameK = ks.length > 0 && ks.every((v) => Math.abs(Number(v) - Number(ks[0])) < 1e-12);
    const first = members[0] || {};
    const last = members[members.length - 1] || first;
    return {
      id: key,
      timestamp: head.timestamp,
      eventLabel: events.length <= 1 ? (events[0] || '—') : `${events[0]} +${events.length - 1}`,
      eventTitle: events.join(' · '),
      bookCount: members.length,
      booksLabel:
        books.length === 1 && books[0].detail
          ? `${books[0].short} ${books[0].detail}`
          : books.map((b) => b.short).join(', '),
      booksTitle: books
        .map((b) => `${b.name}${b.detail ? ` · ${b.detail}` : ''}${b.event ? ` · ${b.event}` : ''}`)
        .join('\n'),
      delta_mid: sameDelta ? Number(deltas[0]) : null,
      deltaTitle: sameDelta
        ? undefined
        : deltas.map((v) => Number(v).toFixed(4)).join(', '),
      R_obs: sameR ? Number(rs[0]) : null,
      K: sameK ? Number(ks[0]) : null,
      kf_p_before: first.kf_p_before,
      kf_p_after: last.kf_p_after ?? last.kf_p_market_end,
      kf_p_market_end: last.kf_p_market_end ?? last.kf_p_after,
      kf_P_before: first.kf_P_before,
      kf_P_after: last.kf_P_after,
      raw: members,
    };
  });
}

function formatChartTooltipTime(value) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const d = new Date(Number(value));
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

/** Sorted observation events for chart tooltips (competitor signals, Entries diagnostics, KF ingest). */
function buildChartObservationTimeline(competitorSignals, entriesDiagnosticsPoints, kfUpdates) {
  const out = [];
  const push = (timeSec, title, eventKey = null) => {
    if (timeSec == null || !title) return;
    const timeMs = Number(timeSec) * 1000;
    if (!Number.isFinite(timeMs)) return;
    out.push({ timeMs, title, event: eventKey });
  };
  for (const s of competitorSignals || []) {
    if (!s?.event || s.event === 'no_move' || s.timestamp == null) continue;
    const book = s.sportsbook || '?';
    push(s.timestamp, `${book} · ${shortCompetitorEventDescription(s.event)}`, s.event);
  }
  for (const r of entriesDiagnosticsPoints || []) {
    if (r?.timestamp == null) continue;
    if (r?.sportsbook && r?.event) {
      push(
        r.timestamp,
        `${r.sportsbook} · ${shortCompetitorEventDescription(r.event)}`,
        r.event,
      );
    } else if (r?.update_type) {
      push(r.timestamp, `Entries · ${String(r.update_type).replace(/_/g, ' ')}`, r.update_type);
    }
  }
  for (const u of kfUpdates || []) {
    if (u?.update_type && u.timestamp != null) {
      push(u.timestamp, `KF · ${String(u.update_type).replace(/_/g, ' ')}`, u.update_type);
    }
  }
  out.sort((a, b) => a.timeMs - b.timeMs);
  return out;
}

function lookupChartObservations(timeline, timeMs, toleranceMs = 5000) {
  if (!timeline?.length || !Number.isFinite(Number(timeMs))) return [];
  const t = Number(timeMs);
  const within = timeline.filter((o) => Math.abs(o.timeMs - t) <= toleranceMs);
  if (within.length > 0) return within;
  let best = null;
  let bestDist = Infinity;
  for (const o of timeline) {
    const d = Math.abs(o.timeMs - t);
    if (d < bestDist) {
      bestDist = d;
      best = o;
    }
  }
  return best && bestDist <= 30000 ? [best] : [];
}

function enrichChartRowsWithObservations(rows, timeline, toleranceMs = 5000) {
  if (!rows?.length || !timeline?.length) return rows || [];
  return rows.map((row) => {
    const timeMs = row.time ?? (row.timestamp != null ? Number(row.timestamp) * 1000 : null);
    const observations = lookupChartObservations(timeline, timeMs, toleranceMs);
    if (!observations.length) return row;
    const inline = [];
    if (row.update_type) {
      inline.push({
        timeMs,
        title: `Entries · ${String(row.update_type).replace(/_/g, ' ')}`,
        event: row.update_type,
      });
    }
    const merged = [...observations];
    for (const o of inline) {
      if (!merged.some((m) => m.title === o.title)) merged.push(o);
    }
    return { ...row, observations: merged };
  });
}

function competitorEventColor(event) {
  const e = String(event || '');
  if (COMPETITOR_EVENT_COLORS[e]) return COMPETITOR_EVENT_COLORS[e];
  if (e === 'entries_fuse' || e === 'panel') return '#38BDF8';
  if (e.startsWith('lc_') || e.startsWith('line_change_')) {
    const inner = e.startsWith('lc_') ? e.slice(3) : e.slice('line_change_'.length);
    return COMPETITOR_EVENT_COLORS[inner] || COMPETITOR_EVENT_COLORS.line_change;
  }
  if (e.startsWith('lfr') || e.startsWith('line_flicker_revert')) {
    return COMPETITOR_EVENT_COLORS.lfr || COMPETITOR_EVENT_COLORS.line_flicker_revert;
  }
  if (e.startsWith('pfr') || e.startsWith('price_flicker_revert')) {
    return COMPETITOR_EVENT_COLORS.pfr || COMPETITOR_EVENT_COLORS.price_flicker_revert;
  }
  return '#CBD5E1';
}

function observationTitleColor(obs) {
  if (obs?.event) return competitorEventColor(obs.event);
  const tail = String(obs?.title || '').split(' · ').pop();
  return competitorEventColor(tail);
}

const CHART_TOOLTIP_MAX_HEIGHT = '70vh';
const CHART_TOOLTIP_MAX_WIDTH = 480;
const CHART_TOOLTIP_Z_INDEX = 99999;

/** Screen position for portaled tooltip: anchor chart's wrapper + Recharts local coordinate. */
function tooltipFixedPositionFromCoordinate(coordinate, chartAnchorRef) {
  if (!coordinate || coordinate.x == null || coordinate.y == null) return null;
  const root = chartAnchorRef?.current;
  if (!root) return null;
  const wrapper = root.querySelector?.('.recharts-wrapper') || root;
  const r = wrapper.getBoundingClientRect();
  return {
    left: r.left + coordinate.x,
    top: r.top + coordinate.y,
  };
}

const CHART_SUSPEND_OVERLAY_SUFFIX = '__suspend';

function isChartablePrice(v) {
  return v != null && typeof v === 'number' && Number.isFinite(v) && v > 1;
}

/** Posted overround for two-sided books (same as KF ``book_implied_vig``). */
function impliedVigFromPrices(priceOver, priceUnder) {
  if (!isChartablePrice(priceOver) || !isChartablePrice(priceUnder)) return null;
  return Math.max(0, (1 / Number(priceOver)) + (1 / Number(priceUnder)) - 1);
}

/** Lookup R from sport / market maps (print name or normalized key). */
function lookupBookR(rMap, sportsbook) {
  if (!rMap || !sportsbook) return null;
  if (rMap[sportsbook] != null && Number(rMap[sportsbook]) > 0) return Number(rMap[sportsbook]);
  const lower = String(sportsbook).trim().toLowerCase();
  if (rMap[lower] != null && Number(rMap[lower]) > 0) return Number(rMap[lower]);
  for (const [k, v] of Object.entries(rMap)) {
    if (String(k).trim().toLowerCase() === lower && Number(v) > 0) return Number(v);
  }
  return null;
}

/**
 * Consensus weight share: w_i = 1/R_i, R from R_twosided (2w) else R_base.
 * Returns fraction of total weight across competitor rows (not Sleeper).
 * When ``liveFracs`` is set (from quote.book_weight_fracs), prefer those.
 */
function assignBookWeightFractions(rows, rBase, rTwosided, liveFracs) {
  if (liveFracs && typeof liveFracs === 'object') {
    const mapped = rows.map((r) => {
      if (r.isOurs) return { ...r, weightFrac: null };
      const direct = liveFracs[r.book];
      let frac = direct != null && Number.isFinite(Number(direct)) ? Number(direct) : null;
      if (frac == null) {
        const lower = String(r.book || '').trim().toLowerCase();
        for (const [k, v] of Object.entries(liveFracs)) {
          if (String(k).trim().toLowerCase() === lower && Number.isFinite(Number(v))) {
            frac = Number(v);
            break;
          }
        }
      }
      return { ...r, weightFrac: frac };
    });
    const sum = mapped.reduce(
      (a, r) => (r.isOurs || r.weightFrac == null ? a : a + r.weightFrac),
      0,
    );
    if (sum > 0) {
      return mapped.map((r) => (
        r.weightFrac == null ? r : { ...r, weightFrac: r.weightFrac / sum }
      ));
    }
    return mapped;
  }
  if (!rBase || !Object.keys(rBase).length) {
    // No writer fracs and no usable R map (api-only config) — omit weights.
    return rows.map((r) => ({ ...r, weightFrac: null }));
  }
  const weights = rows.map((r) => {
    if (r.isOurs) return 0;
    const map = r.twoSided && rTwosided && Object.keys(rTwosided).length
      ? rTwosided
      : rBase;
    let R = lookupBookR(map, r.book);
    if (R == null || !(R > 0)) {
      const vals = Object.values(map || {}).map(Number).filter((v) => v > 0);
      R = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0.05;
    }
    return 1 / Math.max(R, 1e-6);
  });
  const sum = weights.reduce((a, b) => a + b, 0);
  return rows.map((r, i) => ({
    ...r,
    weightFrac: !r.isOurs && sum > 0 ? weights[i] / sum : null,
  }));
}

/** Two-sided for this sportsbook row at this line — from price legs, not book-level KF flag. */
function rowIsTwoSided(row) {
  if (!row) return null;
  const po = row.price_over;
  const pu = row.price_under;
  if (isChartablePrice(po) && isChartablePrice(pu)) return true;
  if (isChartablePrice(po) && !isChartablePrice(pu)) return false;
  if (row.is_two_sided === true) return true;
  if (row.is_two_sided === false) return false;
  return null;
}

function formatPriceHistory(priceData) {
  const byBook = {};
  const marginatedByTs = {};
  (priceData || []).forEach((item) => {
    if (!byBook[item.sportsbook]) {
      byBook[item.sportsbook] = [];
    }
    const isLocked = item.locked === true;
    const po = isChartablePrice(item.price_over) ? item.price_over : null;
    const pu = isChartablePrice(item.price_under) ? item.price_under : null;
    const pf =
      item.p_fair != null && typeof item.p_fair === 'number' && item.p_fair > 0 && item.p_fair < 1
        ? item.p_fair
        : null;
    const panelAsk =
      item.panel_ask != null && Number.isFinite(Number(item.panel_ask))
        ? Number(item.panel_ask)
        : null;
    const panelBid =
      item.panel_bid != null && Number.isFinite(Number(item.panel_bid))
        ? Number(item.panel_bid)
        : null;
    if (po == null && pf == null && panelAsk == null && panelBid == null && !isLocked) return;
    byBook[item.sportsbook].push({
      time: item.timestamp * 1000,
      timestamp: item.timestamp,
      price_over: po,
      price_under: pu,
      p_fair: pf,
      size_over: nullableSize(item.size_over),
      size_under: nullableSize(item.size_under),
      staleness_s: item.staleness_s,
      locked: isLocked,
      is_two_sided: isLocked
        ? false
        : rowIsTwoSided({ price_over: po, price_under: pu, is_two_sided: item.is_two_sided }),
      panel_ask: panelAsk,
      panel_bid: panelBid,
      panel_source_line:
        item.panel_source_line != null && Number.isFinite(Number(item.panel_source_line))
          ? Number(item.panel_source_line)
          : null,
      panel_translated: item.panel_translated === true,
      panel_fair:
        item.panel_fair != null && Number.isFinite(Number(item.panel_fair))
          ? Number(item.panel_fair)
          : null,
      panel_fair_source:
        item.panel_fair_source != null && Number.isFinite(Number(item.panel_fair_source))
          ? Number(item.panel_fair_source)
          : null,
      panel_price_over: isChartablePrice(item.panel_price_over) ? item.panel_price_over : null,
      panel_price_under: isChartablePrice(item.panel_price_under) ? item.panel_price_under : null,
    });
    if (item.marginated_price != null && item.marginated_price > 0) {
      marginatedByTs[item.timestamp] = item.marginated_price;
    }
  });
  Object.keys(byBook).forEach((book) => byBook[book].sort((a, b) => a.timestamp - b.timestamp));
  const allTs = new Set();
  Object.values(byBook).forEach((arr) => arr.forEach((p) => allTs.add(p.timestamp)));
  const sortedTs = Array.from(allTs).sort((a, b) => a - b);
  const bookState = {};
  Object.keys(byBook).forEach((book) => {
    bookState[book] = { lastOver: null, lastPfair: null };
  });
  const data = sortedTs.map((ts) => {
    const point = { time: ts * 1000, timestamp: ts };
    Object.keys(byBook).forEach((book) => {
      const arr = byBook[book];
      let last = null;
      for (let i = arr.length - 1; i >= 0; i--) {
        if (arr[i].timestamp <= ts) {
          last = arr[i];
          break;
        }
      }
      if (!last) return;

      const st = bookState[book];

      point[`${book}_sizeOver`] = last.locked ? null : last.size_over;
      point[`${book}_sizeUnder`] = last.locked ? null : last.size_under;

      // `locked` means suspended/absent at the scrub chart line only. The book may
      // still be live on another KF ref line — keep panel_* so competitor spread
      // can show the Δ-translated bar (e.g. PrizePicks left 24.5, quoting 25.5).
      if (last.locked) {
        point[`${book}_over`] = null;
        point[`${book}_pfair`] = null;
        if (st.lastOver != null || st.lastPfair != null) {
          if (st.lastOver != null) {
            point[`${book}_suspendOver`] = st.lastOver;
          }
          if (st.lastPfair != null) {
            point[`${book}_suspendPfair`] = st.lastPfair;
          }
        }
      } else {
        point[`${book}_suspendOver`] = null;
        point[`${book}_suspendPfair`] = null;

        const raw = last.price_over;
        if (isChartablePrice(raw)) {
          point[`${book}_over`] = raw;
          point[`${book}_two_sided`] = last.is_two_sided;
          st.lastOver = raw;
        } else if (st.lastOver != null) {
          point[`${book}_over`] = null;
        }

        const rawUnder = last.price_under;
        if (isChartablePrice(rawUnder)) {
          point[`${book}_under`] = rawUnder;
        }

        const rawPfair = last.p_fair;
        if (rawPfair != null && typeof rawPfair === 'number' && rawPfair > 0 && rawPfair < 1) {
          point[`${book}_pfair`] = rawPfair;
          st.lastPfair = rawPfair;
        } else if (st.lastPfair != null) {
          point[`${book}_pfair`] = null;
        }
      }

      // Competitor two-sided panel (may be line-translated; not used by Raw Prices).
      if (last.panel_ask != null || last.panel_bid != null || last.panel_fair != null) {
        point[`${book}_panelAsk`] = last.panel_ask;
        point[`${book}_panelBid`] = last.panel_bid;
        point[`${book}_panelLine`] = last.panel_source_line;
        point[`${book}_panelTranslated`] = last.panel_translated === true;
        point[`${book}_panelFair`] = last.panel_fair;
        point[`${book}_panelFairSource`] = last.panel_fair_source;
        point[`${book}_panelPriceOver`] = last.panel_price_over;
        point[`${book}_panelPriceUnder`] = last.panel_price_under;
      }
    });
    if (marginatedByTs[ts] != null) {
      point.marginated_price = marginatedByTs[ts];
    }
    return point;
  });
  const hasMarginated = data.some((p) => p.marginated_price != null);
  const hasPfair = data.some((p) => Object.keys(p).some((k) => k.endsWith('_pfair') && p[k] != null));
  const books = Object.keys(byBook)
    .filter((book) =>
      byBook[book].some(
        (row) => isChartablePrice(row.price_over) || (row.p_fair != null && row.p_fair > 0),
      ),
    )
    .sort(compareBooksForPanel);
  const panelBooks = Object.keys(byBook)
    .filter((book) =>
      byBook[book].some(
        (row) =>
          isChartablePrice(row.price_over) ||
          (row.p_fair != null && row.p_fair > 0) ||
          row.panel_ask != null ||
          row.panel_bid != null,
      ),
    )
    .sort(compareBooksForPanel);
  const hasBookPrices = data.some((p) =>
    books.some(
      (book) =>
        isChartablePrice(p[`${book}_over`]) ||
        (p[`${book}_pfair`] != null && p[`${book}_pfair`] > 0) ||
        isChartablePrice(p[`${book}_suspendOver`]) ||
        (p[`${book}_suspendPfair`] != null && p[`${book}_suspendPfair`] > 0),
    ),
  );
  const bookIsTwoSided = {};
  books.forEach((book) => {
    const arr = byBook[book];
    const latest = arr.length ? arr[arr.length - 1] : null;
    bookIsTwoSided[book] = rowIsTwoSided(latest) === true;
  });
  return { data, books, panelBooks, hasMarginated, hasPfair, hasBookPrices, bookIsTwoSided };
}

function formatAxisTime(value) {
  if (!value) return '';
  const d = new Date(value);
  return d.toLocaleString(undefined, {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function ChartObservationTooltip({
  active,
  payload,
  label,
  coordinate,
  eventTimeline,
  chartAnchorRef,
  formatter,
  contentStyle,
  itemStyle,
  maxHeight = CHART_TOOLTIP_MAX_HEIGHT,
  maxWidth = CHART_TOOLTIP_MAX_WIDTH,
}) {
  const [positionTick, setPositionTick] = useState(0);
  const fixedPos = useMemo(() => {
    if (!active || !coordinate) return null;
    // positionTick is intentional: scroll/resize changes layout without changing coordinate
    void positionTick;
    return tooltipFixedPositionFromCoordinate(coordinate, chartAnchorRef);
  }, [active, coordinate, chartAnchorRef, positionTick]);

  useLayoutEffect(() => {
    if (!active) return undefined;
    const update = () => setPositionTick((t) => t + 1);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [active]);

  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload;
  let observations = point?.observations;
  if (!observations?.length) {
    observations = lookupChartObservations(eventTimeline, label, 5000);
  }
  const style = {
    backgroundColor: 'rgba(15,23,42,0.95)',
    border: '1px solid rgba(96,165,250,0.3)',
    borderRadius: 6,
    color: '#E2E8F0',
    padding: '8px 10px',
    fontSize: '12px',
    lineHeight: 1.35,
    maxHeight,
    maxWidth,
    minWidth: 220,
    overflowY: 'auto',
    overflowX: 'hidden',
    boxSizing: 'border-box',
    pointerEvents: 'auto',
    ...contentStyle,
  };
  const inner = (
    <div style={style}>
      <div style={{ marginBottom: 6, fontWeight: 500 }}>
        {formatChartTooltipTime(label)}
      </div>
      {observations?.length > 0 && (
        <div
          style={{
            marginBottom: 8,
            paddingBottom: 6,
            borderBottom: '1px solid rgba(100,116,139,0.35)',
          }}
        >
          <div
            style={{
              fontSize: '10px',
              color: '#64748B',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              marginBottom: 4,
            }}
          >
            Observation
          </div>
          {observations.map((obs, i) => (
            <div
              key={`${obs.timeMs}-${obs.title}-${i}`}
              style={{
                color: observationTitleColor(obs),
                fontSize: '11px',
                fontWeight: 500,
                marginBottom: i < observations.length - 1 ? 4 : 0,
              }}
            >
              {obs.title}
            </div>
          ))}
        </div>
      )}
      <div>
        {payload.map((entry, i) => {
          const formatted = formatter
            ? formatter(entry.value, entry.name, entry)
            : null;
          const formattedValue = formatted != null ? formatted[0] : entry.value;
          const name = formatted != null ? formatted[1] : entry.name;
          if (formattedValue == null && formattedValue !== 0) return null;
          return (
            <div
              key={entry.name || i}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 12,
                marginTop: 2,
                ...(itemStyle || {}),
              }}
            >
              <span
                style={{
                  color: entry.color || '#94A3B8',
                  flex: '1 1 auto',
                  minWidth: 0,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                {name}
              </span>
              <span style={{ fontWeight: 500, flexShrink: 0, textAlign: 'right' }}>{formattedValue}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  if (fixedPos && typeof document !== 'undefined') {
    return createPortal(
      <div
        className="chart-observation-tooltip-portal"
        style={{
          position: 'fixed',
          left: fixedPos.left,
          top: fixedPos.top,
          zIndex: CHART_TOOLTIP_Z_INDEX,
          pointerEvents: 'auto',
        }}
      >
        {inner}
      </div>,
      document.body,
    );
  }

  return inner;
}

const BOOK_COLORS = {
  'DraftKings': '#60A5FA',
  'FanDuel': '#34D399',
  'Unibet': '#FBBF24',
  'William Hill': '#F87171',
  'Underdog Fantasy (Multipliers)': '#A78BFA',
  'Betway': '#22D3EE',
};

const DIAGNOSTIC_TYPES = [
  { value: 'entries_p_variance', label: 'EntriesKF P' },
  { value: 'n_books', label: 'N Books' },
  { value: 'gating', label: 'Gating' },
  { value: 'z_innovation', label: 'Z Innovation' },
  { value: 'r', label: 'R (eff.)' },
];

/** kf.uncertainty_model v2 stores P_panel in R_eff and the quote-move z² in z2. */
const DIAGNOSTIC_LABELS_V2 = { z_innovation: 'z² (quote moves)', r: 'P_panel' };

function diagnosticLabel(value, isV2) {
  if (isV2 && DIAGNOSTIC_LABELS_V2[value]) return DIAGNOSTIC_LABELS_V2[value];
  return DIAGNOSTIC_TYPES.find((t) => t.value === value)?.label;
}

// Default dashboard config
const DEFAULT_DASHBOARD_CONFIG = {
  chart_lookback_hours: 6,
  chart_max_points: 500,
};

const DASHBOARD_TAB_KEYS = new Set(['competitors', 'risk', 'liability', 'orderBook', 'models', 'uncertainty']);

function lineClose(a, b, eps = 1e-6) {
  return Math.abs(a - b) < eps;
}

/** Roll up ``entry-stats-by-line`` rows to a single stat object per leg line (all P% buckets). */
function aggregateEntryStatsByLineOnly(rows) {
  const groups = new Map();
  if (!Array.isArray(rows)) return groups;
  for (const r of rows) {
    if (r.line == null || !Number.isFinite(Number(r.line))) continue;
    const L = Number(r.line);
    let canon = L;
    for (const k of groups.keys()) {
      if (lineClose(k, L)) {
        canon = k;
        break;
      }
    }
    let g = groups.get(canon);
    if (!g) {
      g = {
        n_entries: 0,
        total_fees: 0,
        total_fees_over: 0,
        total_fees_under: 0,
        liability_net_over: 0,
        liability_net_under: 0,
        over_num_acc: 0,
        over_den_acc: 0,
        under_num_acc: 0,
        under_den_acc: 0,
      };
      groups.set(canon, g);
    }
    g.n_entries += Number(r.n_entries) || 0;
    g.total_fees += Number(r.total_fees) || 0;
    g.total_fees_over += Number(r.total_fees_over) || 0;
    g.total_fees_under += Number(r.total_fees_under) || 0;
    g.liability_net_over += Number(r.liability_net_over) || 0;
    g.liability_net_under += Number(r.liability_net_under) || 0;
    const avgO = r.weighted_avg_decimal_over != null ? Number(r.weighted_avg_decimal_over) : NaN;
    const denO = r.weighted_decimal_over_den != null ? Number(r.weighted_decimal_over_den) : NaN;
    if (Number.isFinite(avgO) && Number.isFinite(denO) && denO > 1e-12) {
      g.over_num_acc += avgO * denO;
      g.over_den_acc += denO;
    }
    const avgU = r.weighted_avg_decimal_under != null ? Number(r.weighted_avg_decimal_under) : NaN;
    const denU = r.weighted_decimal_under_den != null ? Number(r.weighted_decimal_under_den) : NaN;
    if (Number.isFinite(avgU) && Number.isFinite(denU) && denU > 1e-12) {
      g.under_num_acc += avgU * denU;
      g.under_den_acc += denU;
    }
  }
  const out = new Map();
  for (const [canon, g] of groups) {
    const denBlend = g.over_den_acc + g.under_den_acc;
    const weighted_avg_decimal_blend =
      denBlend > 1e-12 ? (g.over_num_acc + g.under_num_acc) / denBlend : null;
    out.set(canon, {
      n_entries: g.n_entries,
      total_fees: g.total_fees,
      total_fees_over: g.total_fees_over,
      total_fees_under: g.total_fees_under,
      liability_net_over: g.liability_net_over,
      liability_net_under: g.liability_net_under,
      weighted_avg_decimal_over:
        g.over_den_acc > 1e-12 ? g.over_num_acc / g.over_den_acc : null,
      weighted_avg_decimal_under:
        g.under_den_acc > 1e-12 ? g.under_num_acc / g.under_den_acc : null,
      weighted_avg_decimal_blend,
    });
  }
  return out;
}

function lookupEntryStatLineOnly(lineVal, byLineMap) {
  const L = Number(lineVal);
  if (!Number.isFinite(L) || !byLineMap || typeof byLineMap.get !== 'function') return null;
  for (const [k, v] of byLineMap.entries()) {
    if (lineClose(k, L)) return v;
  }
  return null;
}

/** Additive margin in probability at quoted over decimal: (1 / dec) − P(over). */
function ladderQuoteMarginationProb(row) {
  const dec = row?.marginated_price != null ? Number(row.marginated_price) : NaN;
  const p = row?.p_over != null ? Number(row.p_over) : NaN;
  if (!Number.isFinite(dec) || dec <= 0 || !Number.isFinite(p)) return null;
  return 1 / dec - p;
}

function formatPreGameHandleLimitUsd(usd) {
  if (usd == null || typeof usd !== 'number' || Number.isNaN(usd)) return null;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(usd);
}

/** ``total_fees`` with muted O/U leg split left, total right (Risk / quote card). */
function renderFeesWithOverUnder(es) {
  if (!es) return '—';
  const t = es.total_fees;
  const tStr = t != null && Number.isFinite(Number(t)) ? Number(t).toFixed(0) : '—';
  const fo = es.total_fees_over;
  const fu = es.total_fees_under;
  const oOk = fo != null && Number.isFinite(Number(fo));
  const uOk = fu != null && Number.isFinite(Number(fu));
  if (!oOk && !uOk) return tStr;
  const oStr = oOk ? Number(fo).toFixed(0) : '—';
  const uStr = uOk ? Number(fu).toFixed(0) : '—';
  return (
    <span className="risk-metric-inline">
      <span className="risk-ou-split">
        O {oStr} · U {uStr}
      </span>
      <span className="risk-metric-primary">{tStr}</span>
    </span>
  );
}

/** Fee-weighted posted decimal with muted O/U side means left, blend right — ladder rollup by line. */
function renderPriceWgtWithOverUnder(st) {
  if (!st) return '—';
  const wo = st.weighted_avg_decimal_over;
  const wu = st.weighted_avg_decimal_under;
  const blend = st.weighted_avg_decimal_blend;
  const oOk = wo != null && Number.isFinite(Number(wo));
  const uOk = wu != null && Number.isFinite(Number(wu));
  const bOk = blend != null && Number.isFinite(Number(blend));
  const tStr = bOk ? Number(blend).toFixed(2) : oOk ? Number(wo).toFixed(2) : uOk ? Number(wu).toFixed(2) : '—';
  if (!oOk && !uOk) return tStr;
  const oStr = oOk ? Number(wo).toFixed(2) : '—';
  const uStr = uOk ? Number(wu).toFixed(2) : '—';
  return (
    <span className="risk-metric-inline">
      <span className="risk-ou-split">
        O {oStr} · U {uStr}
      </span>
      <span className="risk-metric-primary">{tStr}</span>
    </span>
  );
}

/** max(O-side net, U-side net) right; muted O / U breakdown left. */
function renderLiabilityWithOverUnder(st) {
  if (!st) return '—';
  const lo = st.liability_net_over;
  const lu = st.liability_net_under;
  const oOk = lo != null && Number.isFinite(Number(lo));
  const uOk = lu != null && Number.isFinite(Number(lu));
  if (!oOk && !uOk) return '—';
  const oStr = oOk ? Number(lo).toFixed(2) : '—';
  const uStr = uOk ? Number(lu).toFixed(2) : '—';
  let head;
  if (oOk && uOk) head = Math.max(Number(lo), Number(lu)).toFixed(2);
  else if (oOk) head = oStr;
  else head = uStr;
  return (
    <span className="risk-metric-inline">
      <span className="risk-ou-split">
        O {oStr} · U {uStr}
      </span>
      <span className="risk-metric-primary">{head}</span>
    </span>
  );
}

/** Ladder row: O/U decimals left, over decimal as primary right (when both quoted). */
function renderLadderMarginatedOverUnder(row) {
  if (!row) return '—';
  const o = row.marginated_price;
  const u = row.marginated_price_under;
  const oOk = o != null && Number.isFinite(Number(o)) && Number(o) > 0;
  const uOk = u != null && Number.isFinite(Number(u)) && Number(u) > 0;
  if (!oOk && !uOk) return '—';
  const oStr = oOk ? Number(o).toFixed(2) : '—';
  const uStr = uOk ? Number(u).toFixed(2) : '—';
  if (oOk && uOk) {
    return (
      <span className="risk-metric-inline">
        <span className="risk-ou-split risk-ou-split--ladder">
          O {oStr} · U {uStr}
        </span>
        <span className="risk-metric-primary">{oStr}</span>
      </span>
    );
  }
  if (oOk) {
    return (
      <span className="risk-metric-inline">
        <span className="risk-ou-split risk-ou-split--ladder">O</span>
        <span className="risk-metric-primary">{oStr}</span>
      </span>
    );
  }
  return (
    <span className="risk-metric-inline">
      <span className="risk-ou-split risk-ou-split--ladder">U</span>
      <span className="risk-metric-primary">{uStr}</span>
    </span>
  );
}

function DashboardPage() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // UI State
  const [selectedMarket, setSelectedMarket] = useState(null);

  const lineUserPickedRef = useRef(false);
  const lastUrlMarketRef = useRef(null);
  const urlLineSeedDoneRef = useRef(false);
  const quoteChartRef = useRef(null);
  const rawPricesChartRef = useRef(null);
  const diagnosticsChartRef = useRef(null);
  const riskGateVarianceChartRef = useRef(null);

  // Text search inputs (what user is typing)
  const [playerNameInput, setPlayerNameInput] = useState('');
  const [teamNameInput, setTeamNameInput] = useState('');
  
  // Submitted search values (what triggers the API call)
  const [submittedPlayerName, setSubmittedPlayerName] = useState('');
  const [submittedTeamName, setSubmittedTeamName] = useState('');
  
  const [filters, setFilters] = useState({
    league: '', market_id: '',
    min_books: '', max_books: '', gate_allowed: '',
    // Fixture start window (YYYY-MM-DD, either side open); client-side on fixture_start_time.
    start_from: '', start_to: '',
    exclude_live: true,
  });
  const [sortBy, setSortBy] = useState('none');
  
  // Search handler - called on button click or Enter key
  const handleSearch = useCallback(() => {
    setSubmittedPlayerName(playerNameInput);
    setSubmittedTeamName(teamNameInput);
  }, [playerNameInput, teamNameInput]);
  
  // Handle Enter key in search inputs
  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);
  const [selectedDiagnosticBottom, setSelectedDiagnosticBottom] = useState('gating');
  useEffect(() => {
    if (!DIAGNOSTIC_TYPES.some((t) => t.value === selectedDiagnosticBottom)) {
      setSelectedDiagnosticBottom('gating');
    }
  }, [selectedDiagnosticBottom]);
  const [selectedLine, setSelectedLine] = useState(0.5);
  const [priceChartType, setPriceChartType] = useState('price_over');
  const [spreadScrubTs, setSpreadScrubTs] = useState(null);
  // Entry feed filters (dashboard recent entries)
  const [entriesMinSharp, setEntriesMinSharp] = useState('');
  const [entriesMinFee, setEntriesMinFee] = useState('');
  // Dashboard tabs: existing views plus Liability v3.
  const [activeTab, setActiveTab] = useState('competitors');
  /** True after the user clicks a tab; cleared on market select so defaults can apply. */
  const tabUserPickedRef = useRef(false);
  /** Model market opened without books yet — switch to Competitors once books appear. */
  const preferCompetitorsWhenAvailableRef = useRef(false);
  const [v3BetType, setV3BetType] = useState('all');
  const [marketLookupInput, setMarketLookupInput] = useState('');

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (DASHBOARD_TAB_KEYS.has(tabFromUrl) && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams, activeTab]);

  // ==========================================================================
  // React Query Hooks
  // ==========================================================================
  
  // Config (dashboard.chart_lookback_hours / chart_max_points from server, else defaults)
  const { data: configData } = useConfig();
  const dashboardConfig = useMemo(
    () => ({ ...DEFAULT_DASHBOARD_CONFIG, ...configData?.dashboard }),
    [configData?.dashboard]
  );
  const historyHours = dashboardConfig.chart_lookback_hours;
  
  // Markets list - use server-side filtering for better performance
  const marketsQueryParams = useMemo(() => {
    const params = {
      limit: 500,
      exclude_live: filters.exclude_live,
      sort_by: sortBy === 'last_sse_update' ? 'last_sse_update' : 
               sortBy === 'timestamp' ? 'timestamp' : 'last_sse_update',
    };
    // Apply server-side filters where supported
    if (filters.league) params.league = filters.league;
    if (filters.market_id) params.market_id = filters.market_id;
    if (submittedPlayerName) params.player_name = submittedPlayerName;
    if (submittedTeamName) params.team_name = submittedTeamName;
    if (filters.gate_allowed !== '') params.gate_allowed = filters.gate_allowed === 'true';
    if (filters.min_books) params.min_books = parseInt(filters.min_books, 10);
    return params;
  }, [filters.exclude_live, filters.league, filters.market_id, submittedPlayerName, submittedTeamName, filters.gate_allowed, filters.min_books, sortBy]);
  
  const { data: marketsData, isLoading: marketsLoading } = useAllQuotes(marketsQueryParams);
  const markets = useMemo(() => Array.isArray(marketsData) ? marketsData : [], [marketsData]);

  useEffect(() => {
    const marketFromUrl = searchParams.get('market');
    if (!marketFromUrl) return;
    const lineFromUrlRaw = searchParams.get('line');
    const lineFromUrl = lineFromUrlRaw != null ? Number(lineFromUrlRaw) : null;
    const urlMarketChanged = lastUrlMarketRef.current !== marketFromUrl;
    if (urlMarketChanged) {
      lastUrlMarketRef.current = marketFromUrl;
      lineUserPickedRef.current = false;
      urlLineSeedDoneRef.current = false;
    }
    setSelectedMarket(marketFromUrl);
    setMarketLookupInput(marketFromUrl);
    if (lineFromUrl != null && Number.isFinite(lineFromUrl) && !urlLineSeedDoneRef.current) {
      setSelectedLine(lineFromUrl);
      lineUserPickedRef.current = true;
      urlLineSeedDoneRef.current = true;
      return;
    }
    const m = markets.find((x) => x.market_key === marketFromUrl);
    if (m?.line != null && !urlLineSeedDoneRef.current) {
      setSelectedLine(m.line);
      urlLineSeedDoneRef.current = true;
    }
  }, [searchParams, markets]);

  // Filter options (leagues used in dropdown; teams/players hooks run for cache)
  const { data: leaguesData } = useFilterLeagues();
  useFilterTeams();
  useFilterPlayers();
  const availableLeagues = leaguesData || [];
  const { data: marketIdOptions } = useFilterMarketIds();
  const availableMarketIds = useMemo(
    () => (Array.isArray(marketIdOptions) ? [...marketIdOptions].sort() : []),
    [marketIdOptions]
  );

  // Selected market data - all queries enabled only when a market is selected
  const { data: currentQuote, isLoading: quoteLoading } = useQuote(selectedMarket);
  const isUncertaintyV2 = currentQuote?.uncertainty_detail?.model === 'v2';

  const modelRulesScope = useMemo(() => {
    const sport = currentQuote?.sport || 'basketball';
    const league = currentQuote?.league || 'wnba';
    return {
      sport,
      league,
      sport_bucket: sportBucketForFixture({ sport, league }) || 'wnba',
    };
  }, [currentQuote?.sport, currentQuote?.league]);

  const { data: modelRulesData } = useModelMarketRules(modelRulesScope);
  const modelRules = useMemo(
    () => (Array.isArray(modelRulesData?.rules) ? modelRulesData.rules : []),
    [modelRulesData],
  );

  const booklessModelsTabHint = useMemo(
    () => isBooklessModelMarket(null, currentQuote, modelRules),
    [currentQuote, modelRules],
  );

  const booklessDerivedHint = booklessModelsTabHint;

  const {
    data: derivedModelDetail,
    isLoading: derivedModelLoading,
    isError: derivedModelIsError,
    error: derivedModelError,
  } = useDerivedModelDetail(selectedMarket, {
    enabled: Boolean(selectedMarket),
    refetchInterval: booklessDerivedHint ? 10_000 : 15_000,
  });

  const booklessDerived = useMemo(
    () => isBooklessDerivedMarket(derivedModelDetail, currentQuote),
    [derivedModelDetail, currentQuote],
  );

  const booklessModelsTab = useMemo(
    () => isBooklessModelMarket(derivedModelDetail, currentQuote, modelRules),
    [derivedModelDetail, currentQuote, modelRules],
  );

  const { data: quoteHistoryRaw } = useQuoteHistory(selectedMarket, historyHours, {
    refetchInterval: booklessDerived ? 10_000 : 30_000,
  });
  const { data: diagnosticsGatingRaw } = useQuoteDiagnosticsHistory(selectedMarket, historyHours, 'gating');
  const varianceDiagnosticType =
    currentQuote?.quote_source === 'entries' ? 'entries_p_variance' : 'p_variance';
  const { data: diagnosticsVarianceRiskRaw } = useQuoteDiagnosticsHistory(
    selectedMarket,
    historyHours,
    varianceDiagnosticType,
    { enabled: !!selectedMarket && (activeTab === 'risk' || activeTab === 'competitors') }
  );
  const { data: diagnosticsBottomRaw } = useQuoteDiagnosticsHistory(
    selectedMarket,
    historyHours,
    selectedDiagnosticBottom === 'entries_p_variance' ? 'gating' : selectedDiagnosticBottom,
    { enabled: !!selectedMarket && selectedDiagnosticBottom !== 'entries_p_variance' },
  );
  const { data: competitorSignalsData } = useCompetitorSignals(selectedMarket, historyHours, {}, {
    // Mute only stops Entries competitor sync — still fetch for the Competitors tab when books quote.
    enabled: !!selectedMarket,
  });
  const { data: entriesDiagnosticsRaw } = useEntriesDiagnosticsHistory(
    selectedMarket,
    historyHours,
    'P_post',
    { enabled: !!selectedMarket && (activeTab === 'competitors' || activeTab === 'risk') }
  );
  const {
    data: linesData,
    isFetched: linesFetched,
    isError: linesError,
  } = useQuoteLines(selectedMarket, historyHours);
  const { data: ladderData } = useQuoteLadder(selectedMarket, historyHours);
  const { data: entryStatsByLinePayload } = useEntryStatsByLine(selectedMarket, {
    enabled: !!selectedMarket && activeTab === 'risk',
  });
  const { data: updatesData } = useQuoteUpdates(selectedMarket, historyHours);
  const { data: masterActivityRiskData } = useMasterActivityLog(
    selectedMarket,
    { hours: historyHours, limit: 800 },
    { enabled: !!selectedMarket && activeTab === 'risk' }
  );
  const { data: marketEntries = [] } = useMarketEntries(selectedMarket, {
    limit: 30,
    min_sharp: entriesMinSharp ? parseFloat(entriesMinSharp) : undefined,
    min_fee: entriesMinFee ? parseFloat(entriesMinFee) : undefined,
  });
  const {
    data: v3DashboardData,
    isLoading: v3DashboardLoading,
    error: v3DashboardError,
  } = useMarketDashboard(selectedMarket, {
    enabled: Boolean(selectedMarket && activeTab === 'liability'),
  });

  // ==========================================================================
  // Derived State (memoized transformations)
  // ==========================================================================
  
  const availableLines = useMemo(() => {
    const raw = Array.isArray(linesData) ? linesData : [];
    return [...raw].map(Number).filter((x) => Number.isFinite(x)).sort((a, b) => a - b);
  }, [linesData]);

  /** Lines query settled — empty [] is valid for bookless / muted competitor markets. */
  const linesQuerySettled = Boolean(selectedMarket) && (linesFetched || linesError);

  /**
   * Avoid fetching raw prices at soccer default 0.5 before WNBA/basketball line list loads.
   * Once lines are known empty (model/derived with competitors off), do not stay on "Loading lines…".
   */
  const priceHistoryLineReady = useMemo(() => {
    if (!selectedMarket) return false;
    if (availableLines.length > 0) {
      return availableLines.some((l) => lineClose(l, selectedLine));
    }
    if (linesQuerySettled) {
      return true;
    }
    const quoteLine = currentQuote?.line;
    if (quoteLine != null && Number.isFinite(Number(quoteLine))) {
      return lineClose(selectedLine, Number(quoteLine));
    }
    return false;
  }, [
    selectedMarket,
    availableLines,
    selectedLine,
    currentQuote?.line,
    linesQuerySettled,
  ]);

  const noCompetitorRawPrices = useMemo(() => {
    // Bookless / no SSE print history — mute alone must not blank the Competitors view.
    if (linesQuerySettled && availableLines.length === 0) return true;
    return false;
  }, [linesQuerySettled, availableLines.length]);

  /** Model / derived targets: Competitors tab only when books are actually linked. */
  const isModelDashboardMarket = useMemo(() => {
    if (booklessModelsTab) return true;
    const mid = marketIdFromMarketKey(selectedMarket);
    if (mid === 'goalie_fpts') return true;
    const quoteMid =
      String(currentQuote?.market_id || '').trim() || mid;
    const league = currentQuote?.league;
    return findApplicableModelRule(modelRules, quoteMid, league) != null;
  }, [booklessModelsTab, selectedMarket, currentQuote?.market_id, currentQuote?.league, modelRules]);

  /** Show Models tab only when a derived/YAML model actually applies to this quote. */
  const showModelsTab = useMemo(() => {
    if (booklessModelsTab || booklessDerived) return true;
    if (derivedModelDetail?.derived_model_available) return true;
    const mid =
      String(currentQuote?.market_id || '').trim() ||
      marketIdFromMarketKey(selectedMarket);
    if (mid === 'goalie_fpts') return true;
    return findApplicableModelRule(modelRules, mid, currentQuote?.league) != null;
  }, [
    booklessModelsTab,
    booklessDerived,
    derivedModelDetail?.derived_model_available,
    currentQuote?.market_id,
    currentQuote?.league,
    selectedMarket,
    modelRules,
  ]);

  // Model/derived: hide Competitors only when no books are quoting (mute is Entries-only).
  const showCompetitorsTab = useMemo(() => {
    if (!isModelDashboardMarket) return true;
    if (Number(currentQuote?.n_books ?? 0) > 0) return true;
    if (linesQuerySettled && availableLines.length > 0) return true;
    return false;
  }, [
    isModelDashboardMarket,
    currentQuote?.n_books,
    linesQuerySettled,
    availableLines.length,
  ]);

  useEffect(() => {
    if (searchParams.get('tab') !== 'signals') return;
    const fallback = showCompetitorsTab ? 'competitors' : (showModelsTab ? 'models' : 'risk');
    const next = new URLSearchParams(searchParams);
    next.set('tab', fallback);
    setSearchParams(next, { replace: true });
    setActiveTab(fallback);
  }, [searchParams, showCompetitorsTab, showModelsTab, setSearchParams]);

  /**
   * Follow the quote unless the user pinned a rung: sending no line lets the server target
   * each sample's own ``kf_quotes.line``, so panel implieds sit on the baseline we were
   * quoting at that timestamp (no-op translation when a book posts it) and a lookback across
   * a baseline shift stays consistent. Pinning keeps the raw traces a same-line comparison.
   */
  const priceHistoryLine = lineUserPickedRef.current ? selectedLine : null;
  /*
   * The server builds `panel_*` for a single sample (the competitor panel renders one
   * timestamp), so the scrub position has to reach it. Debounced: scrubbing fires on every
   * mouse move, and each distinct ts is its own query key / refetch.
   */
  const [panelScrubTs, setPanelScrubTs] = useState(null);
  useEffect(() => {
    const handle = setTimeout(() => setPanelScrubTs(spreadScrubTs), 250);
    return () => clearTimeout(handle);
  }, [spreadScrubTs]);
  const { data: priceHistoryRaw } = useQuotePriceHistory(
    selectedMarket,
    historyHours,
    priceHistoryLine,
    { enabled: !!selectedMarket && priceHistoryLineReady },
    panelScrubTs,
  );

  // Filter markets - only apply client-side filters not handled by server
  // Server-side: league, market_id, player_name, team_name, gate_allowed, min_books, exclude_live, sort_by
  // Client-side only: max_books, start_from / start_to
  const filteredMarkets = useMemo(() => {
    let filtered = [...markets];
    if (filters.max_books) {
      filtered = filtered.filter(m => m.n_books <= parseInt(filters.max_books, 10));
    }
    // Fixture start window. Bounds are local calendar days, inclusive on both ends, so `to`
    // extends to that day's midnight. A market with no fixture_start_time can't be shown to
    // satisfy a bound, so it drops out once either side is set.
    const fromMs = dateFromInputValue(filters.start_from)?.getTime() ?? null;
    const toDay = dateFromInputValue(filters.start_to);
    const toMs = toDay ? toDay.getTime() + 24 * 60 * 60 * 1000 : null;
    if (fromMs != null || toMs != null) {
      filtered = filtered.filter((m) => {
        const startS = Number(m.fixture_start_time);
        if (!Number.isFinite(startS) || startS <= 0) return false;
        const startMs = startS * 1000;
        if (fromMs != null && startMs < fromMs) return false;
        if (toMs != null && startMs >= toMs) return false;
        return true;
      });
    }
    return filtered;
  }, [markets, filters.max_books, filters.start_from, filters.start_to]);

  // Any narrowing choice reveals the market strip, not just the name/team text search: picking a
  // Type or League returns a usable set on its own, and gating the strip on the text inputs alone
  // hid tiles while the count above them read e.g. "53 / 53". ``exclude_live`` is excluded because
  // it defaults to on — counting it would render the strip unfiltered on first load, which is what
  // the gate exists to prevent.
  const hasActiveFilter = Boolean(
    submittedPlayerName
    || submittedTeamName
    || filters.league
    || filters.market_id
    || filters.min_books
    || filters.max_books
    || filters.gate_allowed !== ''
    || filters.start_from
    || filters.start_to
  );

  const selectedQuoteForCharts = useMemo(
    () =>
      currentQuote ||
      filteredMarkets.find((m) => m.market_key === selectedMarket) ||
      markets.find((m) => m.market_key === selectedMarket),
    [currentQuote, filteredMarkets, markets, selectedMarket]
  );

  const siblingFixtureId = useMemo(() => {
    const fid = selectedQuoteForCharts?.fixture_id;
    if (fid != null && String(fid).trim() !== '') return String(fid);
    if (!selectedMarket) return null;
    const parts = String(selectedMarket).split(':');
    return parts.length >= 3 && parts[0] ? parts[0] : null;
  }, [selectedQuoteForCharts?.fixture_id, selectedMarket]);

  const siblingPlayerId = useMemo(() => {
    const pid = selectedQuoteForCharts?.player_id;
    if (pid != null && String(pid).trim() !== '') return String(pid);
    if (!selectedMarket) return null;
    const parts = String(selectedMarket).split(':');
    return parts.length >= 3 && parts[1] ? String(parts[1]) : null;
  }, [selectedQuoteForCharts?.player_id, selectedMarket]);

  const { data: fixtureQuotesRaw, isLoading: fixtureQuotesLoading } = useQuotesByFixture(siblingFixtureId, {
    enabled: Boolean(selectedMarket && siblingFixtureId && siblingPlayerId),
  });

  const samePlayerGameMarkets = useMemo(() => {
    const mergeAndSort = (rows) => {
      if (!siblingPlayerId) return [];
      const byKey = new Map();
      for (const q of rows) {
        if (!q || !q.market_key) continue;
        if (String(q.player_id) !== siblingPlayerId) continue;
        byKey.set(q.market_key, q);
      }
      const list = [...byKey.values()];
      list.sort((a, b) => {
        const ma = String(a.market_id || '');
        const mb = String(b.market_id || '');
        if (ma !== mb) return ma.localeCompare(mb);
        const la = a.line != null ? Number(a.line) : NaN;
        const lb = b.line != null ? Number(b.line) : NaN;
        if (Number.isFinite(la) && Number.isFinite(lb) && la !== lb) return la - lb;
        if (Number.isFinite(la) && !Number.isFinite(lb)) return -1;
        if (!Number.isFinite(la) && Number.isFinite(lb)) return 1;
        return String(a.market_key).localeCompare(String(b.market_key));
      });
      return list;
    };

    const apiRows = Array.isArray(fixtureQuotesRaw) ? fixtureQuotesRaw : [];
    let sourceRows = apiRows;
    let list = mergeAndSort(sourceRows);
    if (list.length === 0 && siblingFixtureId && siblingPlayerId) {
      sourceRows = markets.filter((m) => {
        if (!m?.market_key) return false;
        const fid =
          m.fixture_id != null && String(m.fixture_id).trim() !== ''
            ? String(m.fixture_id)
            : String(m.market_key).split(':')[0];
        const pid =
          m.player_id != null && String(m.player_id).trim() !== ''
            ? String(m.player_id)
            : String(m.market_key).split(':')[1];
        return fid === siblingFixtureId && pid === siblingPlayerId;
      });
      list = mergeAndSort(sourceRows);
    }

    if (
      selectedMarket &&
      !list.some((q) => q.market_key === selectedMarket) &&
      selectedQuoteForCharts &&
      selectedQuoteForCharts.market_key === selectedMarket &&
      String(selectedQuoteForCharts.player_id) === siblingPlayerId
    ) {
      list = mergeAndSort([...list, selectedQuoteForCharts]);
    }
    return list;
  }, [
    fixtureQuotesRaw,
    markets,
    siblingFixtureId,
    siblingPlayerId,
    selectedMarket,
    selectedQuoteForCharts,
  ]);

  // Quote history formatted for chart
  const quoteHistory = useMemo(() => {
    return (quoteHistoryRaw || []).map(item => ({
      time: item.timestamp * 1000,
      timestamp: item.timestamp,
      p_fair: item.p_fair ?? null,
      p_lower: item.p_lower ?? null,
      p_upper: item.p_upper ?? null,
      base_line: item.base_line ?? null,
    }));
  }, [quoteHistoryRaw]);

  // Detect baseline shift points (where base_line changes)
  const baselineShiftPoints = useMemo(() => {
    const shifts = [];
    for (let i = 1; i < quoteHistory.length; i++) {
      const prev = quoteHistory[i - 1].base_line;
      const curr = quoteHistory[i].base_line;
      if (prev != null && curr != null && Math.abs(prev - curr) >= 0.01) {
        shifts.push({
          time: quoteHistory[i].time,
          oldLine: prev,
          newLine: curr,
        });
      }
    }
    return shifts;
  }, [quoteHistory]);

  // Current base_line from latest history point
  const currentBaseLine = useMemo(() => {
    if (quoteHistory.length > 0) {
      return quoteHistory[quoteHistory.length - 1].base_line;
    }
    return null;
  }, [quoteHistory]);

  useEffect(() => {
    if (!selectedMarket) return;

    const prefLine =
      currentQuote?.line != null && Number.isFinite(currentQuote.line)
        ? currentQuote.line
        : currentBaseLine != null && Number.isFinite(currentBaseLine)
          ? currentBaseLine
          : null;

    if (!availableLines.length) {
      // Bookless / no SSE books: adopt quote line so we don't stay stuck on soccer 0.5.
      if (
        prefLine != null &&
        Number.isFinite(Number(prefLine)) &&
        !lineClose(selectedLine, Number(prefLine))
      ) {
        setSelectedLine(Number(prefLine));
      }
      return;
    }

    const lineInAvailable = (line) => availableLines.some((l) => lineClose(l, line));

    /**
     * Snap to the rung nearest our quote line, never to ``availableLines[0]``.
     * The KF baseline is median-derived so it rarely equals a posted line exactly, and the
     * lowest posted rung can be hundreds of units away (McCaffrey: quote 948.5, DK ladder
     * bottom 749.5) — which silently opened the chart there and translated every book onto it.
     */
    const nearestAvailable = (target) =>
      availableLines.reduce(
        (best, l) => (Math.abs(l - target) < Math.abs(best - target) ? l : best),
        availableLines[0]
      );

    const pickDefaultLine = () =>
      prefLine != null && Number.isFinite(Number(prefLine))
        ? nearestAvailable(Number(prefLine))
        : availableLines[0];

    const defaultLine = pickDefaultLine();
    const needsStructuralReset = selectedLine === 0.5 || !lineInAvailable(selectedLine);

    if (needsStructuralReset) {
      setSelectedLine(defaultLine);
      return;
    }

    // Baseline moved: re-sync to the rung nearest the new quote line (exact match would
    // leave the chart parked wherever it was whenever the baseline is unposted).
    if (!lineUserPickedRef.current && prefLine != null && Number.isFinite(Number(prefLine))) {
      const hit = nearestAvailable(Number(prefLine));
      if (hit !== undefined && !lineClose(selectedLine, hit)) setSelectedLine(hit);
    }
  }, [selectedMarket, availableLines, selectedLine, currentQuote?.line, currentBaseLine]);

  // Diagnostics (gating) formatted for chart
  const diagnosticsHistory = useMemo(() => {
    return (diagnosticsGatingRaw || []).map(item => {
      let value = item.value;
      value = (value === true || value === 'true' || value === 1) ? 1 : 0;
      return {
        time: item.timestamp * 1000,
        timestamp: item.timestamp,
        value,
        by_book: item.by_book || null,
        n_books: item.n_books,
        gate_reason: item.gate_reason || null,
      };
    });
  }, [diagnosticsGatingRaw]);

  // Diagnostics (bottom chart) formatted
  const diagnosticsHistoryBottom = useMemo(() => {
    return mapDiagnosticsForChart(diagnosticsBottomRaw || [], selectedDiagnosticBottom);
  }, [diagnosticsBottomRaw, selectedDiagnosticBottom]);
  /** Posterior variance for charts: prefer ``kf_diagnostics.P_variance_post`` (same rows/timestamps as gating). Fallbacks when diagnostics empty. */
  const riskVarianceHistory = useMemo(() => {
    const srcLabel = currentQuote?.quote_source === 'entries' ? 'entries' : 'base';
    const fromDiag = (diagnosticsVarianceRiskRaw || [])
      .map((item) => ({
        timestamp: Number(item.timestamp),
        p_variance: item.value != null ? Number(item.value) : NaN,
        source: srcLabel,
      }))
      .filter((row) => Number.isFinite(row.p_variance))
      .sort((a, b) => a.timestamp - b.timestamp);
    if (fromDiag.length > 0) {
      return fromDiag;
    }
    if (currentQuote?.quote_source === 'entries') {
      const fromQuotes = (quoteHistoryRaw || [])
        .map((item) => ({
          timestamp: Number(item.timestamp),
          p_variance: item.P != null ? Number(item.P) : NaN,
          source: 'entries',
        }))
        .filter((row) => Number.isFinite(row.p_variance))
        .sort((a, b) => a.timestamp - b.timestamp);
      if (fromQuotes.length > 0) {
        return fromQuotes;
      }
    }
    const events = Array.isArray(masterActivityRiskData?.events) ? masterActivityRiskData.events : [];
    const entriesP = events
      .filter((evt) => evt?.stream === 'entries_kf' && Number.isFinite(Number(evt?.ts)))
      .map((evt) => ({
        timestamp: Number(evt.ts),
        p_variance: Number(evt?.payload?.P_post),
        source: 'entries',
      }))
      .filter((row) => Number.isFinite(row.p_variance));
    if (entriesP.length > 0) {
      return entriesP.sort((a, b) => a.timestamp - b.timestamp);
    }
    return [];
  }, [
    currentQuote?.quote_source,
    quoteHistoryRaw,
    masterActivityRiskData?.events,
    diagnosticsVarianceRiskRaw,
  ]);
  /** One point per gating diagnostic timestamp; P variance forward-filled from variance series (handles sub-ms skew + manual rows). */
  const riskGateVarianceHistory = useMemo(() => {
    const gates = (diagnosticsHistory || [])
      .filter((row) => Number.isFinite(Number(row?.timestamp)))
      .sort((a, b) => Number(a.timestamp) - Number(b.timestamp));
    const vars = (riskVarianceHistory || [])
      .filter((row) => Number.isFinite(Number(row?.timestamp)))
      .sort((a, b) => Number(a.timestamp) - Number(b.timestamp));
    const EPS = 1e-5;
    let vi = 0;
    let lastVar = null;
    let lastSrc = null;
    const out = [];
    for (const g of gates) {
      const ts = Number(g.timestamp);
      while (vi < vars.length && vars[vi].timestamp <= ts + EPS) {
        lastVar = vars[vi].p_variance;
        lastSrc = vars[vi].source;
        vi += 1;
      }
      out.push({
        time: ts * 1000,
        timestamp: ts,
        gate: g.value,
        gate_reason: g.gate_reason || null,
        p_variance: lastVar,
        variance_source: lastSrc,
      });
    }
    return out;
  }, [diagnosticsHistory, riskVarianceHistory]);

  // Price history formatted
  const priceHistory = useMemo(() => formatPriceHistory(priceHistoryRaw || []), [priceHistoryRaw]);

  const chartObservationTimeline = useMemo(
    () => buildChartObservationTimeline(
      competitorSignalsData?.signals,
      entriesDiagnosticsRaw?.points,
      Array.isArray(updatesData) ? updatesData : [],
    ),
    [competitorSignalsData, entriesDiagnosticsRaw, updatesData],
  );

  const competitorSignalRows = useMemo(
    () => summarizeCompetitorSignals(competitorSignalsData?.signals).slice(0, 40),
    [competitorSignalsData?.signals],
  );

  const quoteHistoryForChart = useMemo(
    () => enrichChartRowsWithObservations(quoteHistory, chartObservationTimeline),
    [quoteHistory, chartObservationTimeline],
  );

  const modelFusePoints = useMemo(() => {
    const hist = derivedModelDetail?.derived_model_output_history;
    if (!Array.isArray(hist)) return [];
    return hist
      .map((row) => {
        const ts = parseModelOutputTimestamp(row.timestamp);
        const p = row.p_fair_after ?? row.p_fair_obs;
        if (ts == null || p == null || !Number.isFinite(Number(p))) return null;
        return { time: ts * 1000, p_fair: Number(p) };
      })
      .filter(Boolean);
  }, [derivedModelDetail?.derived_model_output_history]);

  const modelsQuoteHistoryForChart = useMemo(() => {
    const balanced = derivedModelDetail?.model_diagnostics?.fpts?.balanced_line;
    return enrichQuoteHistoryWithModelOutputs(
      quoteHistory,
      derivedModelDetail?.derived_model_output_history,
      balanced,
    );
  }, [quoteHistory, derivedModelDetail?.derived_model_output_history, derivedModelDetail?.model_diagnostics?.fpts?.balanced_line]);

  const priceHistoryForChart = useMemo(() => {
    if (!priceHistory?.data?.length) return priceHistory;
    const enriched = enrichChartRowsWithObservations(priceHistory.data, chartObservationTimeline);
    const last = enriched[enriched.length - 1];
    const nowMs = Date.now();
    const lastMs = last?.time != null ? Number(last.time) : null;
    // Carry last quote forward to "now" so the series reaches the present (display-only).
    if (last != null && lastMs != null && Number.isFinite(lastMs) && nowMs - lastMs > 1500) {
      return {
        ...priceHistory,
        data: [
          ...enriched,
          {
            ...last,
            time: nowMs,
            timestamp: nowMs / 1000,
            observations: undefined,
            _liveTip: true,
          },
        ],
      };
    }
    return {
      ...priceHistory,
      data: enriched,
    };
  }, [priceHistory, chartObservationTimeline]);

  const entriesVarianceChart = useMemo(() => {
    const rows = entriesDiagnosticsRaw?.points || [];
    return rows
      .filter((r) => r.value != null && r.timestamp != null)
      .map((r) => ({
        time: Number(r.timestamp) * 1000,
        value: Number(r.value),
        update_type: r.update_type,
        z2: r.z2,
        p_prob_post: r.p_prob_after,
        p_prob_delta: r.p_prob_delta,
        p_variance_delta: r.p_variance_delta,
        event: r.event,
        sportsbook: r.sportsbook,
        applied: r.applied,
      }));
  }, [entriesDiagnosticsRaw]);

  const entriesVarianceChartForChart = useMemo(
    () => enrichChartRowsWithObservations(entriesVarianceChart, chartObservationTimeline),
    [entriesVarianceChart, chartObservationTimeline],
  );

  const diagnosticsHistoryBottomForChart = useMemo(
    () => enrichChartRowsWithObservations(diagnosticsHistoryBottom, chartObservationTimeline),
    [diagnosticsHistoryBottom, chartObservationTimeline],
  );

  const diagnosticsBottomChartData = useMemo(() => {
    if (selectedDiagnosticBottom === 'entries_p_variance') {
      if (entriesVarianceChartForChart.length > 0) return entriesVarianceChartForChart;
      return enrichChartRowsWithObservations(
        mapDiagnosticsForChart(diagnosticsVarianceRiskRaw || [], 'p_variance'),
        chartObservationTimeline,
      );
    }
    return diagnosticsHistoryBottomForChart;
  }, [
    selectedDiagnosticBottom,
    entriesVarianceChartForChart,
    diagnosticsVarianceRiskRaw,
    diagnosticsHistoryBottomForChart,
    chartObservationTimeline,
  ]);

  const riskGateVarianceHistoryForChart = useMemo(
    () => enrichChartRowsWithObservations(riskGateVarianceHistory, chartObservationTimeline),
    [riskGateVarianceHistory, chartObservationTimeline],
  );

  const chartTooltipWrapperProps = useMemo(
    () => ({
      wrapperStyle: { visibility: 'hidden', pointerEvents: 'none' },
      allowEscapeViewBox: { x: false, y: true },
    }),
    [],
  );

  const makeChartTooltip = useCallback(
    (formatter, extraProps = {}) => {
      const { chartAnchorRef, ...tooltipExtra } = extraProps;
      return (props) => (
        <ChartObservationTooltip
          {...props}
          {...tooltipExtra}
          chartAnchorRef={chartAnchorRef}
          eventTimeline={chartObservationTimeline}
          formatter={formatter}
        />
      );
    },
    [chartObservationTimeline],
  );

  // Reset price chart type if p_fair not available
  useEffect(() => {
    if (priceChartType === 'p_fair' && !priceHistory.hasPfair) {
      setPriceChartType('price_over');
    }
  }, [priceChartType, priceHistory.hasPfair]);

  // ==========================================================================
  // Helper Functions
  // ==========================================================================

  function mapDiagnosticsForChart(rows, diagnosticType) {
    return (rows || []).map(item => {
      let value = item.value;
      if (diagnosticType === 'gating') value = (value === true || value === 'true' || value === 1) ? 1 : 0;
      if (diagnosticType === 'n_books') {
        const raw = value != null ? value : item.n_books;
        value = raw != null ? Number(raw) : null;
      }
      return {
        time: item.timestamp * 1000,
        timestamp: item.timestamp,
        value,
        by_book: item.by_book || null,
        n_books: item.n_books,
      };
    });
  }

  // Price history helpers live at module scope (isChartablePrice / formatPriceHistory).

  const spreadSnapshot = useMemo(() => {
    const data = priceHistory?.data;
    const panelBooks = priceHistory?.panelBooks?.length
      ? priceHistory.panelBooks
      : priceHistory?.books;
    if (!data || !data.length || !panelBooks?.length) return null;

    // Scrub by timestamp so refresh / sparse rebuilds do not jump back to an old index.
    let idx = data.length - 1;
    if (spreadScrubTs != null && Number.isFinite(Number(spreadScrubTs))) {
      const target = Number(spreadScrubTs);
      let best = data.length - 1;
      let bestDt = Infinity;
      for (let i = 0; i < data.length; i++) {
        const ts = data[i]?.timestamp != null
          ? Number(data[i].timestamp)
          : (data[i]?.time != null ? Number(data[i].time) / 1000 : null);
        if (ts == null || !Number.isFinite(ts)) continue;
        const dt = Math.abs(ts - target);
        if (dt < bestDt) {
          bestDt = dt;
          best = i;
        }
      }
      idx = best;
    }

    const point = data[idx];
    const atLatest = idx === data.length - 1;
    const pointTs = point?.timestamp != null
      ? Number(point.timestamp)
      : (point?.time != null ? Number(point.time) / 1000 : null);

    // p_fair for Sleeper row — live quote at latest; else nearest quote-history / ladder.
    let ourPFair = null;
    if (atLatest && currentQuote?.p_fair != null && Number.isFinite(Number(currentQuote.p_fair))) {
      ourPFair = Number(currentQuote.p_fair);
    } else if (pointTs != null && quoteHistory?.length) {
      let best = null;
      let bestDt = Infinity;
      for (const q of quoteHistory) {
        const qts = q.timestamp != null ? Number(q.timestamp) : (q.time != null ? Number(q.time) / 1000 : null);
        if (qts == null || q.p_fair == null || !Number.isFinite(Number(q.p_fair))) continue;
        const dt = Math.abs(qts - pointTs);
        if (dt < bestDt) {
          bestDt = dt;
          best = Number(q.p_fair);
        }
      }
      if (best != null && bestDt <= 120) ourPFair = best;
    }

    const ladderRows = Array.isArray(ladderData) ? ladderData : [];
    const ourQuote = buildSleeperSpreadRow({
      atLatest,
      currentQuote,
      selectedLine,
      point,
      pointTs,
      quoteHistory,
      ladderRows,
      ourPFair,
    });

    const chartLine = Number.isFinite(Number(selectedLine)) ? Number(selectedLine) : null;
    const rows = panelBooks.map((book) => {
      const panelAsk = point[`${book}_panelAsk`];
      const panelBid = point[`${book}_panelBid`];
      const panelFair = point[`${book}_panelFair`];
      const hasPanel = panelAsk != null || panelBid != null || panelFair != null;
      const over = point[`${book}_over`];
      const under = point[`${book}_under`];
      const ask = hasPanel
        ? (panelAsk != null && Number.isFinite(Number(panelAsk)) ? Number(panelAsk) : null)
        : (isChartablePrice(over) ? 1 / over : null);
      const bid = hasPanel
        ? (panelBid != null && Number.isFinite(Number(panelBid)) ? Number(panelBid) : null)
        : (isChartablePrice(under) ? 1 - 1 / under : null);
      const bookFair = panelFair != null && Number.isFinite(Number(panelFair))
        ? Number(panelFair)
        : (point[`${book}_pfair`] != null && Number.isFinite(Number(point[`${book}_pfair`]))
          ? Number(point[`${book}_pfair`])
          : null);
      const bookFairSource = point[`${book}_panelFairSource`] != null
        && Number.isFinite(Number(point[`${book}_panelFairSource`]))
        ? Number(point[`${book}_panelFairSource`])
        : bookFair;
      const priceOver = isChartablePrice(point[`${book}_panelPriceOver`])
        ? point[`${book}_panelPriceOver`]
        : (isChartablePrice(over) ? over : null);
      const priceUnder = isChartablePrice(point[`${book}_panelPriceUnder`])
        ? point[`${book}_panelPriceUnder`]
        : (isChartablePrice(under) ? under : null);
      const sourceLine = point[`${book}_panelLine`] != null
        ? Number(point[`${book}_panelLine`])
        : chartLine;
      const translated = point[`${book}_panelTranslated`] === true
        || (sourceLine != null && chartLine != null && Math.abs(sourceLine - chartLine) >= 0.01);
      const twoSided = isChartablePrice(priceOver) && isChartablePrice(priceUnder);
      const vig = impliedVigFromPrices(priceOver, priceUnder);
      // Don't treat p_fair fill as a posted bid/ask — only real under/over legs.
      const postedAsk = isChartablePrice(priceOver) && ask != null ? ask : null;
      const postedBid = isChartablePrice(priceUnder) && bid != null ? bid : null;
      return {
        book,
        sizeOver: point[`${book}_sizeOver`] ?? null,
        sizeUnder: point[`${book}_sizeUnder`] ?? null,
        ask: postedAsk,
        bid: postedBid,
        bookFair,
        bookFairSource,
        priceOver,
        priceUnder,
        twoSided,
        vig,
        isOurs: false,
        sourceLine,
        translated,
        bidQuoted: postedBid != null,
        askQuoted: postedAsk != null,
      };
    }).filter((r) => r.ask != null || r.bid != null || r.bookFair != null);

    const liveFracs = currentQuote?.book_weight_fracs
      ? currentQuote.book_weight_fracs
      : null;
    // Prefer quote fracs from the owning writer profile. Never fall back to
    // api-only GET /config kf.R_* (wrong sport → DraftKings wildly overweighted).
    const weightedRows = assignBookWeightFractions(
      rows,
      {},
      {},
      liveFracs,
    );

    const ourWithVig = ourQuote
      ? {
        ...ourQuote,
        vig: impliedVigFromPrices(ourQuote.priceOver, ourQuote.priceUnder),
        weightFrac: null,
      }
      : null;

    const allRows = [
      ...(ourWithVig ? [ourWithVig] : []),
      ...weightedRows,
    ];
    return { idx, point, rows: allRows, n: data.length, atLatest, pointTs };
  }, [
    priceHistory,
    spreadScrubTs,
    selectedLine,
    quoteHistory,
    ladderData,
    currentQuote,
  ]);

  const formatPlayerName = (m) => (m && m.player_name) ? m.player_name : ((m && m.player_id) || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  
  const formatTime = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };
  
  const formatMarketLabel = (marketId) => (marketId || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim() || '—';

  // Counts render whole; yards keep a decimal so a residual half-line stays visible.
  const formatRecordedValue = (v) => {
    if (v == null || Number.isNaN(Number(v))) return '—';
    const n = Number(v);
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
  };

  // ==========================================================================
  // Event Handlers
  // ==========================================================================

  const handleRefresh = useCallback(() => {
    // Invalidate all queries to force refetch
    queryClient.invalidateQueries();
  }, [queryClient]);

  const handleDashboardTabClick = useCallback((tab) => {
    if (!DASHBOARD_TAB_KEYS.has(tab)) return;
    tabUserPickedRef.current = true;
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleSelectMarket = useCallback((marketKey) => {
    if (!marketKey) return;
    lineUserPickedRef.current = false;
    tabUserPickedRef.current = false;
    preferCompetitorsWhenAvailableRef.current = false;
    setSelectedMarket(marketKey);
    setSpreadScrubTs(null);
    setMarketLookupInput(marketKey);
    const parts = String(marketKey).split(':');
    const mid = parts.length >= 3 ? parts[2] : marketIdFromMarketKey(marketKey);
    const nextParams = { market: marketKey };
    const m = markets.find((x) => x.market_key === marketKey);
    const isModelish =
      mid === 'goalie_fpts' ||
      findApplicableModelRule(modelRules, mid, m?.league) != null;
    if (isModelish) {
      const hasBooks = Number(m?.n_books ?? 0) > 0;
      if (hasBooks) {
        setActiveTab('competitors');
        nextParams.tab = 'competitors';
      } else {
        setActiveTab('models');
        nextParams.tab = 'models';
        preferCompetitorsWhenAvailableRef.current = true;
      }
    }
    setSearchParams(nextParams, { replace: true });
    setSelectedLine(m && m.line != null ? m.line : 0.5);
  }, [markets, setSearchParams, modelRules]);

  useEffect(() => {
    if (!selectedMarket || !isModelDashboardMarket) return;
    if (!showCompetitorsTab && activeTab === 'competitors') {
      preferCompetitorsWhenAvailableRef.current = true;
      const fallback = showModelsTab ? 'models' : 'risk';
      const next = new URLSearchParams(searchParams);
      next.set('tab', fallback);
      setSearchParams(next, { replace: true });
      setActiveTab(fallback);
    }
  }, [
    selectedMarket,
    isModelDashboardMarket,
    showCompetitorsTab,
    showModelsTab,
    activeTab,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!preferCompetitorsWhenAvailableRef.current) return;
    if (!selectedMarket || !isModelDashboardMarket) return;
    if (!showCompetitorsTab || tabUserPickedRef.current) return;
    preferCompetitorsWhenAvailableRef.current = false;
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'competitors');
    setSearchParams(next, { replace: true });
    setActiveTab('competitors');
  }, [
    selectedMarket,
    isModelDashboardMarket,
    showCompetitorsTab,
    searchParams,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!selectedMarket || showModelsTab || activeTab !== 'models') return;
    const fallback = showCompetitorsTab ? 'competitors' : 'risk';
    const next = new URLSearchParams(searchParams);
    next.set('tab', fallback);
    setSearchParams(next, { replace: true });
    setActiveTab(fallback);
  }, [
    selectedMarket,
    showModelsTab,
    showCompetitorsTab,
    activeTab,
    searchParams,
    setSearchParams,
  ]);

  const applyMarketLookup = useCallback(() => {
    const q = marketLookupInput.trim();
    if (!q) return;
    const exact =
      markets.find((m) => m.market_key === q) ||
      filteredMarkets.find((m) => m.market_key === q);
    if (exact) {
      handleSelectMarket(exact.market_key);
      return;
    }
    const qLower = q.toLowerCase();
    const partial = filteredMarkets.find((m) =>
      String(m.market_key || '').toLowerCase().includes(qLower)
    );
    if (partial) {
      handleSelectMarket(partial.market_key);
    }
  }, [marketLookupInput, markets, filteredMarkets, handleSelectMarket]);

  const handleLineChange = useCallback((newLine) => {
    lineUserPickedRef.current = true;
    setSelectedLine(newLine);
    setSpreadScrubTs(null);
  }, []);

  // Keep competitor-pricing scrub in sync with clicks on the raw prices chart (by timestamp).
  const handleRawPricesChartClick = useCallback((state) => {
    if (state == null) return;
    const rows = priceHistory?.data;
    if (!rows?.length) return;

    const setFromIdx = (i) => {
      const row = rows[i];
      if (!row) return;
      const ts = row.timestamp != null
        ? Number(row.timestamp)
        : (row.time != null ? Number(row.time) / 1000 : null);
      if (ts == null || !Number.isFinite(ts)) return;
      // Follow live tip when clicking the latest point.
      if (i >= rows.length - 1) setSpreadScrubTs(null);
      else setSpreadScrubTs(ts);
    };

    let idx = state.activeTooltipIndex;
    if (idx != null && Number.isFinite(Number(idx))) {
      setFromIdx(Number(idx));
      return;
    }
    const label = state.activeLabel;
    if (label == null) return;
    const target = Number(label);
    if (!Number.isFinite(target)) return;
    let best = 0;
    let bestDt = Infinity;
    for (let i = 0; i < rows.length; i++) {
      const t = rows[i]?.time != null ? Number(rows[i].time) : null;
      if (t == null || !Number.isFinite(t)) continue;
      const dt = Math.abs(t - target);
      if (dt < bestDt) {
        bestDt = dt;
        best = i;
      }
    }
    setFromIdx(best);
  }, [priceHistory?.data]);

  // ==========================================================================
  // Derived display values
  // ==========================================================================

  const selectedMarketLabel = selectedQuoteForCharts
    ? `${formatMarketLabel(selectedQuoteForCharts.market_id)} · Line ${selectedQuoteForCharts.line != null ? selectedQuoteForCharts.line : '—'}`
    : '';
  
  const rawStartTime = currentQuote?.fixture_start_time;
  const fixtureStartTimeMs = (
    rawStartTime != null && 
    typeof rawStartTime === 'number' && 
    !isNaN(rawStartTime) && 
    isFinite(rawStartTime) &&
    rawStartTime > 0
  ) ? rawStartTime * 1000 : null;
  const fixtureStatus = currentQuote?.fixture_status;
  const showStartLine = fixtureStartTimeMs != null && !isNaN(fixtureStartTimeMs) && fixtureStartTimeMs < Date.now();

  const modelsProbChartProps = useMemo(() => {
    if (!booklessDerived) return null;
    return {
      data: modelsQuoteHistoryForChart,
      baselineShiftPoints,
      currentBaseLine,
      fixtureStartTimeMs,
      showStartLine,
      formatAxisTime,
      makeChartTooltip,
      chartTooltipWrapperProps,
      modelFusePoints,
    };
  }, [
    booklessDerived,
    modelsQuoteHistoryForChart,
    baselineShiftPoints,
    currentBaseLine,
    fixtureStartTimeMs,
    showStartLine,
    makeChartTooltip,
    chartTooltipWrapperProps,
    modelFusePoints,
  ]);

  const ladderHistory = useMemo(
    () => (Array.isArray(ladderData) ? ladderData : []),
    [ladderData]
  );
  const latestLadderSnapshot = useMemo(() => {
    if (!ladderHistory.length) return null;
    const latestTs = Math.max(...ladderHistory.map((r) => r.timestamp || 0));
    const rows = ladderHistory
      .filter((r) => r.timestamp === latestTs)
      .sort((a, b) => (a.line || 0) - (b.line || 0));
    return rows.length > 0 ? { latestTs, rows } : null;
  }, [ladderHistory]);
  const entryStatsByLineRows = useMemo(
    () => (Array.isArray(entryStatsByLinePayload?.lines) ? entryStatsByLinePayload.lines : []),
    [entryStatsByLinePayload?.lines]
  );
  const entryStatsAggregatedByLine = useMemo(
    () => aggregateEntryStatsByLineOnly(entryStatsByLineRows),
    [entryStatsByLineRows]
  );
  const riskLiabilityBreakdown = useMemo(() => {
    const es = currentQuote?.entry_stats;
    if (!es) return null;
    const nEntries = Number.isFinite(Number(es.n_entries)) ? Number(es.n_entries) : 0;
    const totalFees = Number.isFinite(Number(es.total_fees)) ? Number(es.total_fees) : 0;
    const over = Number.isFinite(Number(es.liability_net_over)) ? Number(es.liability_net_over) : null;
    const under = Number.isFinite(Number(es.liability_net_under)) ? Number(es.liability_net_under) : null;
    const handleLimit =
      Number.isFinite(Number(es.pre_game_handle_limit_usd)) && Number(es.pre_game_handle_limit_usd) > 0
        ? Number(es.pre_game_handle_limit_usd)
        : null;
    const utilization = handleLimit != null ? totalFees / handleLimit : null;
    let sideAtRisk = null;
    if (over != null && under != null) {
      sideAtRisk = over > under ? 'Over' : over < under ? 'Under' : 'Balanced';
    } else if (over != null) {
      sideAtRisk = 'Over';
    } else if (under != null) {
      sideAtRisk = 'Under';
    }
    const worstCaseLiability =
      over != null && under != null
        ? Math.max(over, under)
        : over != null
          ? over
          : under != null
            ? under
            : null;
    const avgSharp = Number.isFinite(Number(es.avg_sharp_score)) ? Number(es.avg_sharp_score) : null;
    const baselineLine = Number.isFinite(Number(es.baseline_line)) ? Number(es.baseline_line) : null;
    const totalFeesOver = Number.isFinite(Number(es.total_fees_over)) ? Number(es.total_fees_over) : null;
    const totalFeesUnder = Number.isFinite(Number(es.total_fees_under)) ? Number(es.total_fees_under) : null;
    return {
      nEntries,
      totalFees,
      totalFeesOver,
      totalFeesUnder,
      handleLimit,
      utilization,
      avgSharp,
      over,
      under,
      sideAtRisk,
      worstCaseLiability,
      baselineLine,
    };
  }, [currentQuote?.entry_stats]);
  /** Quote / entry_stats baseline for ladder row highlight when liability card is absent. */
  const effectiveBaselineLine = useMemo(() => {
    if (riskLiabilityBreakdown?.baselineLine != null) return riskLiabilityBreakdown.baselineLine;
    const bl = currentQuote?.entry_stats?.baseline_line;
    if (Number.isFinite(Number(bl))) return Number(bl);
    const ql = currentQuote?.line;
    return Number.isFinite(Number(ql)) ? Number(ql) : null;
  }, [
    riskLiabilityBreakdown?.baselineLine,
    currentQuote?.entry_stats?.baseline_line,
    currentQuote?.line,
  ]);
  const orderBookLine = lineUserPickedRef.current ? selectedLine : effectiveBaselineLine;
  const updatesHistory = useMemo(
    () => (Array.isArray(updatesData) ? updatesData : []).filter((row) => {
      const typ = String(row?.update_type || '').trim().toLowerCase();
      const detail = String(row?.update_detail || '').trim().toLowerCase();
      if (typ === 'tick' || detail === 'tick') return false;
      return true;
    }),
    [updatesData]
  );

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div className="dashboard-page">
      <header className="dashboard-header">
        <h1>Market Dashboard</h1>
        <button className="btn-refresh" onClick={handleRefresh}>Refresh</button>
      </header>

      <section className="filters-row">
        <div className="filters-main">
          <div className="filter-group">
            <label>League</label>
            <select value={filters.league} onChange={e => setFilters(f => ({ ...f, league: e.target.value }))}>
              <option value="">All</option>
              {availableLeagues.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="filter-group filter-group-wide">
            <label>Type</label>
            <select
              value={filters.market_id}
              onChange={e => setFilters(f => ({ ...f, market_id: e.target.value }))}
              title="Market type (market_id) — filtered server-side, so this pulls every market of that type"
            >
              <option value="">All</option>
              {availableMarketIds.map(id => <option key={id} value={id}>{marketLabel(id)}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>Team</label>
            <input type="text" value={teamNameInput} onChange={e => setTeamNameInput(e.target.value)} onKeyDown={handleSearchKeyDown} placeholder="Team name" />
          </div>
          <div className="filter-group">
            <label>Player</label>
            <input type="text" value={playerNameInput} onChange={e => setPlayerNameInput(e.target.value)} onKeyDown={handleSearchKeyDown} placeholder="Player name" />
          </div>
          <div className="filter-group filter-num">
            <label>Min Books</label>
            <input type="number" value={filters.min_books} onChange={e => setFilters(f => ({ ...f, min_books: e.target.value }))} placeholder="0" min={0} />
          </div>
          <div className="filter-group filter-num">
            <label>Max Books</label>
            <input type="number" value={filters.max_books} onChange={e => setFilters(f => ({ ...f, max_books: e.target.value }))} placeholder="—" min={0} />
          </div>
          <div className="filter-group">
            <label>Gate</label>
            <select value={filters.gate_allowed} onChange={e => setFilters(f => ({ ...f, gate_allowed: e.target.value }))}>
              <option value="">All</option>
              <option value="true">Allowed</option>
              <option value="false">Blocked</option>
            </select>
          </div>
          <div className="filter-group filter-date">
            <label>Start from</label>
            <input
              type="date"
              value={filters.start_from}
              onChange={e => setFilters(f => ({ ...f, start_from: e.target.value }))}
              title="Only fixtures starting on or after this day (blank = no lower bound)"
            />
          </div>
          <div className="filter-group filter-date">
            <label>Start to</label>
            <input
              type="date"
              value={filters.start_to}
              onChange={e => setFilters(f => ({ ...f, start_to: e.target.value }))}
              title="Only fixtures starting on or before this day (blank = no upper bound)"
            />
          </div>
          <div className="filter-group">
            <label>Sort</label>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
              <option value="none">None</option>
              <option value="last_sse_update">Last SSE</option>
              <option value="timestamp">Timestamp</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Live</label>
            <select value={filters.exclude_live ? 'true' : 'false'} onChange={e => setFilters(f => ({ ...f, exclude_live: e.target.value === 'true' }))}>
              <option value="true">Exclude</option>
              <option value="false">Include</option>
            </select>
          </div>
        </div>
        <div className="filters-actions">
          <span className="filter-count">{filteredMarkets.length} / {markets.length}</span>
          <button className="btn-clear" onClick={() => { setPlayerNameInput(''); setTeamNameInput(''); setSubmittedPlayerName(''); setSubmittedTeamName(''); setFilters({ league: '', market_id: '', min_books: '', max_books: '', gate_allowed: '', start_from: '', start_to: '', exclude_live: true }); setSortBy('none'); }}>Clear</button>
          <button type="button" className="btn-search" onClick={handleSearch}>Search</button>
        </div>
      </section>

      {hasActiveFilter && (
        <section className="market-strip" aria-label="Filtered markets">
          <span className="strip-label">Select market</span>
          <div className="market-strip-inner">
            {marketsLoading ? (
              <div className="empty-state compact">Loading…</div>
            ) : filteredMarkets.length === 0 ? (
              <div className="empty-state compact">{markets.length === 0 ? 'No markets' : 'No match'}</div>
            ) : (
              filteredMarkets.map((market) => (
                <button
                  key={market.market_key}
                  type="button"
                  className={`market-pill ${selectedMarket === market.market_key ? 'active' : ''}`}
                  onClick={() => handleSelectMarket(market.market_key)}
                >
                  <span className="pill-player">{formatPlayerName(market)}</span>
                  <span className="pill-meta">
                    {market.team_name || market.fixture_id || '—'} · Line {market.line != null ? market.line : '—'}
                    {market.p_fair != null ? ` · ${(market.p_fair * 100).toFixed(1)}%` : ''}
                    {market.recorded_state === 'known' && market.recorded_value != null ? (
                      <span
                        className="pill-recorded"
                        title={`Line ${market.line} − recorded ${formatRecordedValue(market.recorded_value)} = residual ${formatRecordedValue(market.residual_line)} (${market.recorded_stat_key || 'stat'})`}
                      >
                        {` · rec ${formatRecordedValue(market.recorded_value)} → ${formatRecordedValue(market.residual_line)}`}
                      </span>
                    ) : market.recorded_state ? (
                      <span className="pill-recorded pill-recorded--bad" title={`Recorded stat ${market.recorded_state}`}>
                        {` · rec ${market.recorded_state}`}
                      </span>
                    ) : null}
                  </span>
                </button>
              ))
            )}
          </div>
        </section>
      )}

      <section className="market-lookup" aria-label="Market lookup">
        <div className="market-lookup-row">
          <label className="market-lookup-label" htmlFor="dashboard-market-key-input">
            Market key
          </label>
          <input
            id="dashboard-market-key-input"
            type="text"
            className="market-lookup-input"
            value={marketLookupInput}
            onChange={(e) => setMarketLookupInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyMarketLookup();
            }}
            placeholder="fixture:player:market_id"
            spellCheck={false}
            autoComplete="off"
          />
          <button type="button" className="btn-search market-lookup-go" onClick={applyMarketLookup}>
            Go
          </button>
        </div>
      </section>

      {!selectedMarket ? (
        <div className="card empty-state">Enter a market key and click Go</div>
      ) : (
        <>
          <div className="dashboard-context-sticky">
            <section className="dashboard-market-hero" aria-label="Selected market">
              <div className="market-hero-top-row">
                <div className="market-hero-copy">
                  <div className="market-hero-primary">
                    <span className="market-hero-player">{formatPlayerName(selectedQuoteForCharts)}</span>
                    <span className="market-hero-market-type">{formatMarketLabel(selectedQuoteForCharts?.market_id)}</span>
                    <span className="market-hero-line-pill">
                      Line {selectedQuoteForCharts?.line != null ? selectedQuoteForCharts.line : '—'}
                    </span>
                  </div>
                  {siblingFixtureId ? (
                    <Link
                      className="market-hero-fixture"
                      title={String(siblingFixtureId)}
                      to={`/game/${encodeURIComponent(siblingFixtureId)}`}
                    >
                      {selectedQuoteForCharts?.team_name || siblingFixtureId}
                    </Link>
                  ) : (
                    <div className="market-hero-fixture">
                      {selectedQuoteForCharts?.team_name || selectedQuoteForCharts?.fixture_id || '—'}
                    </div>
                  )}
                </div>
                {(samePlayerGameMarkets.length > 1 ||
                  (samePlayerGameMarkets.length <= 1 &&
                    fixtureQuotesLoading &&
                    siblingFixtureId &&
                    siblingPlayerId)) && (
                  <div className="market-hero-market-switch">
                    {samePlayerGameMarkets.length > 1 && (
                      <label className="market-hero-sibling-label">
                        <span className="market-hero-sibling-label-text">Same player · game</span>
                        <select
                          className="market-hero-market-select"
                          value={selectedMarket}
                          onChange={(e) => handleSelectMarket(e.target.value)}
                          aria-label="Switch market for this player in this fixture"
                        >
                          {samePlayerGameMarkets.map((q) => (
                            <option key={q.market_key} value={q.market_key}>
                              {`${formatMarketLabel(q.market_id)} · Line ${q.line != null ? q.line : '—'}`}
                              {q.p_fair != null && Number.isFinite(Number(q.p_fair))
                                ? ` · ${(Number(q.p_fair) * 100).toFixed(1)}%`
                                : ''}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                    {samePlayerGameMarkets.length <= 1 &&
                      fixtureQuotesLoading &&
                      siblingFixtureId &&
                      siblingPlayerId && (
                        <span className="market-hero-sibling-loading" aria-live="polite">
                          Loading related markets…
                        </span>
                      )}
                  </div>
                )}
              </div>
            </section>

            <nav className="dashboard-tabs" role="tablist">
              {showCompetitorsTab && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'competitors'}
                  className={`dashboard-tab ${activeTab === 'competitors' ? 'active' : ''}`}
                  onClick={() => handleDashboardTabClick('competitors')}
                >
                  Competitors
                </button>
              )}
              {booklessModelsTab && !showCompetitorsTab && showModelsTab && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'models'}
                  className={`dashboard-tab ${activeTab === 'models' ? 'active' : ''}`}
                  onClick={() => handleDashboardTabClick('models')}
                >
                  Models
                </button>
              )}
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'risk'}
                className={`dashboard-tab ${activeTab === 'risk' ? 'active' : ''}`}
                onClick={() => handleDashboardTabClick('risk')}
              >
                Risk
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'uncertainty'}
                className={`dashboard-tab ${activeTab === 'uncertainty' ? 'active' : ''}`}
                onClick={() => handleDashboardTabClick('uncertainty')}
              >
                Uncertainty
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'liability'}
                className={`dashboard-tab ${activeTab === 'liability' ? 'active' : ''}`}
                onClick={() => handleDashboardTabClick('liability')}
              >
                Liability
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'orderBook'}
                className={`dashboard-tab ${activeTab === 'orderBook' ? 'active' : ''}`}
                onClick={() => handleDashboardTabClick('orderBook')}
              >
                Order Book View
              </button>
              {showModelsTab && (!booklessModelsTab || showCompetitorsTab) && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'models'}
                  className={`dashboard-tab ${activeTab === 'models' ? 'active' : ''}`}
                  onClick={() => handleDashboardTabClick('models')}
                >
                  Models
                </button>
              )}
            </nav>
          </div>

          <section className="current-quote-row">
            <div className="card quote-card">
              <h3>Current Quote</h3>
              {quoteLoading && !currentQuote ? (
                <div className="empty-state compact">Loading quote…</div>
              ) : !currentQuote ? (
                <div className="empty-state compact">Quote unavailable</div>
              ) : (
                <>
                  <div className="quote-grid">
                    <div><span className="quote-label">Market</span><span className="quote-val">{selectedMarketLabel}</span></div>
                    <div>
                      <span className="quote-label">Fixture</span>
                      <span className="quote-val">
                        {currentQuote.fixture_id ? (
                          <Link
                            to={`/game/${encodeURIComponent(currentQuote.fixture_id)}`}
                            style={{ color: 'inherit', textDecoration: 'underline' }}
                          >
                            {currentQuote.team_name || currentQuote.fixture_id}
                          </Link>
                        ) : (
                          currentQuote.team_name || '—'
                        )}
                        {fixtureStatus === 'complete' && <span className="badge badge-success" style={{ marginLeft: '0.5rem' }}>DONE</span>}
                      </span>
                    </div>
                    <div><span className="quote-label">Start (local)</span><span className="quote-val">{fixtureStartTimeMs ? formatTime(fixtureStartTimeMs) : '—'}</span></div>
                    {currentQuote.recorded_state && (
                      <div>
                        <span className="quote-label">Recorded</span>
                        <span className="quote-val">
                          {currentQuote.recorded_state === 'known' ? (
                            <>
                              <strong>{formatRecordedValue(currentQuote.recorded_value)}</strong>
                              <span className="subtle" style={{ marginLeft: '0.35rem' }}>
                                {currentQuote.recorded_stat_key}
                              </span>
                              {currentQuote.residual_line != null && (
                                <span
                                  className="badge"
                                  style={{ marginLeft: '0.5rem', backgroundColor: '#1e3a5f', color: '#93c5fd' }}
                                  title={`Line ${currentQuote.line} − recorded ${formatRecordedValue(currentQuote.recorded_value)} = residual ${formatRecordedValue(currentQuote.residual_line)}`}
                                >
                                  residual {formatRecordedValue(currentQuote.residual_line)}
                                </span>
                              )}
                              {currentQuote.residual_line != null && currentQuote.residual_line <= 0 && (
                                <span className="badge badge-warning" style={{ marginLeft: '0.35rem' }}>
                                  DECIDED
                                </span>
                              )}
                            </>
                          ) : (
                            <span
                              className="badge"
                              style={{ backgroundColor: '#7f1d1d', color: '#fecaca' }}
                              title={
                                currentQuote.recorded_state === 'stale'
                                  ? 'Stat feed has not been confirmed recently — not usable for pricing'
                                  : 'No confirmed stat source for this fixture — not usable for pricing'
                              }
                            >
                              {currentQuote.recorded_state.toUpperCase()}
                            </span>
                          )}
                          {currentQuote.recorded_source && (
                            <span className="subtle" style={{ marginLeft: '0.4rem', fontSize: '0.75rem' }}>
                              {currentQuote.recorded_source}
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                    <div><span className="quote-label">Side</span><span className="quote-val">{currentQuote.quote_two_sided ? <span className="badge badge-success">Two-sided</span> : <span className="badge" style={{ backgroundColor: '#475569', color: '#E2E8F0' }}>One-sided</span>}</span></div>
                    <div className="quote-grid-scenario">
                      <span className="quote-label">Scenario</span>
                      <span className="quote-val">
                        {currentQuote.quote_scenario_label || currentQuote.quote_scenario ? (
                        <span
                          className="badge"
                          style={{ backgroundColor: '#1e3a5f', color: '#93c5fd' }}
                          title={currentQuote.quote_scenario || currentQuote.quote_scenario_label || ''}
                        >
                          {(currentQuote.quote_scenario_label || currentQuote.quote_scenario || '—')
                            .replace(/majority/gi, 'maj.')
                            .replace(/mainly /gi, '')}
                        </span>
                        ) : (
                          <span style={{ color: '#94A3B8' }}>—</span>
                        )}
                        <span
                          className="badge"
                          style={{ backgroundColor: '#14532d', color: '#86efac' }}
                          title="Two-sided books in panel"
                        >
                          {currentQuote.scenario_n_two_sided != null ? currentQuote.scenario_n_two_sided : '—'} 2w
                        </span>
                        <span
                          className="badge"
                          style={{ backgroundColor: '#422006', color: '#fdba74' }}
                          title="One-sided books in panel"
                        >
                          {currentQuote.scenario_n_one_sided != null ? currentQuote.scenario_n_one_sided : '—'} 1w
                        </span>
                      </span>
                    </div>
                    <div><span className="quote-label">P(fair over)</span><span className="quote-val">{(currentQuote.p_fair * 100).toFixed(2)}%</span></div>
                    <div><span className="quote-label">90% CI</span><span className="quote-val">[{(currentQuote.p_lower * 100).toFixed(2)}%, {(currentQuote.p_upper * 100).toFixed(2)}%]</span></div>
                    {(() => {
                      const es = currentQuote.entry_stats;
                      if (!es) return null;
                      const limitStr = formatPreGameHandleLimitUsd(es.pre_game_handle_limit_usd);
                      const hasEntryAgg = (es.n_entries ?? 0) > 0;
                      const showFeesRow = hasEntryAgg || limitStr != null;
                      const feesTitle =
                        'Total = net sum of currency_amount on entries at the quote baseline line. O / U = fee mass on over vs under legs (same scope).';
                      const capTitle =
                        'Pre-game handle cap (USD) from sport_market_limits when configured for this sport, market, and player.';
                      return (
                        <>
                          {hasEntryAgg && (
                            <>
                              <div><span className="quote-label">Avg. sharp (entries)</span><span className="quote-val">{es.avg_sharp_score != null ? es.avg_sharp_score.toFixed(2) : '—'}</span></div>
                              <div><span className="quote-label">Entries</span><span className="quote-val">{es.n_entries ?? 0}</span></div>
                            </>
                          )}
                          {showFeesRow && (
                            <>
                              <div title={feesTitle}>
                                <span className="quote-label" title={feesTitle}>
                                  Fees
                                </span>
                                <span className="quote-val" title={feesTitle}>
                                  {renderFeesWithOverUnder(es)}
                                </span>
                              </div>
                              <div title={capTitle}>
                                <span className="quote-label" title={capTitle}>
                                  Pre-game cap
                                </span>
                                <span className="quote-val" title={capTitle}>
                                  {limitStr != null ? limitStr : '—'}
                                </span>
                              </div>
                            </>
                          )}
                          {hasEntryAgg && Number.isFinite(Number(es.baseline_line)) && (
                            <div className="quote-grid-baseline-lede">
                              <strong style={{ color: '#E2E8F0' }}>
                                Baseline {Number(es.baseline_line).toFixed(2)}
                              </strong>
                            </div>
                          )}
                          {hasEntryAgg && (
                            <div title="Σ E[profit|over wins] − fees on under · independence / payload probs">
                              <span className="quote-label">Liability (over)</span>
                              <span className="quote-val">
                                {es.liability_net_over != null ? Number(es.liability_net_over).toFixed(2) : '—'}
                              </span>
                            </div>
                          )}
                          {hasEntryAgg && (
                            <div title="Σ E[profit|under wins] − fees on over · independence / payload probs">
                              <span className="quote-label">Liability (under)</span>
                              <span className="quote-val">
                                {es.liability_net_under != null ? Number(es.liability_net_under).toFixed(2) : '—'}
                              </span>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    <div><span className="quote-label">Marg. Price (Over)</span><span className="quote-val">{currentQuote.marginated_price != null && currentQuote.marginated_price > 0 ? currentQuote.marginated_price.toFixed(2) : '—'}</span></div>
                    {currentQuote.marginated_price_under != null && currentQuote.marginated_price_under > 0 && (
                      <div><span className="quote-label">Marg. Price (Under)</span><span className="quote-val">{currentQuote.marginated_price_under.toFixed(2)}</span></div>
                    )}
                    <div><span className="quote-label">Books</span><span className="quote-val">{currentQuote.n_books}</span></div>
                    <div>
                      <span className="quote-label">Gate</span>
                      <span className="quote-val">
                        {(() => {
                          const g = getQuoteGateDisplay(currentQuote);
                          return (
                            <>
                              <span className={`badge ${g.badgeClass}`}>{g.badgeText}</span>
                              {g.detail ? (
                                <span className="quote-val-secondary" style={{ display: 'block', marginTop: 4, fontSize: '0.8rem', fontWeight: 400 }}>
                                  {g.detail}
                                </span>
                              ) : null}
                            </>
                          );
                        })()}
                      </span>
                    </div>
                    {currentQuote.rotowire_lineup_state != null && currentQuote.rotowire_lineup_state !== '' && (
                      <div>
                        <span className="quote-label">Rotowire lineup</span>
                        <span className="quote-val" style={{ fontWeight: 500 }}>
                          {rotowireLineupLabel(currentQuote.rotowire_lineup_state) || currentQuote.rotowire_lineup_state}
                        </span>
                      </div>
                    )}
                    {!currentQuote.gate_allowed && (currentQuote.marginated_price_if_gated != null || currentQuote.marginated_price_under_if_gated != null) && (
                      <div><span className="quote-label">If unblocked</span><span className="quote-val quote-val-secondary">
                        {currentQuote.marginated_price_if_gated != null && <span>Over {Number(currentQuote.marginated_price_if_gated).toFixed(2)}{currentQuote.line != null ? ` @ ${Number(currentQuote.line)}` : ''}</span>}
                        {currentQuote.marginated_price_if_gated != null && currentQuote.marginated_price_under_if_gated != null && ' · '}
                        {currentQuote.marginated_price_under_if_gated != null && <span>Under {Number(currentQuote.marginated_price_under_if_gated).toFixed(2)}</span>}
                      </span></div>
                    )}
                  </div>
                </>
              )}
            </div>
          </section>

          {activeTab === 'competitors' && (
          <>
          <div className="chart-section charts-two-col competitors-top-charts">
            <div className="card chart-card chart-card-compact">
              <h3>
                True Probability + CI
                {currentBaseLine != null && <span className="chart-h3-meta"> · Line {currentBaseLine}</span>}
                {baselineShiftPoints.length > 0 && (
                  <span className="chart-h3-meta chart-h3-meta-warn">
                    ({baselineShiftPoints.length} line shift{baselineShiftPoints.length > 1 ? 's' : ''})
                  </span>
                )}
              </h3>
              {quoteHistoryForChart.length > 0 ? (
                <div ref={quoteChartRef} className="chart-tooltip-anchor">
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={quoteHistoryForChart} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(96,165,250,0.2)" />
                    <XAxis dataKey="time" type="number" scale="time" domain={['dataMin', 'dataMax']} stroke="#94A3B8" tickFormatter={formatAxisTime} tick={{ fontSize: 9 }} />
                    <YAxis yAxisId="left" stroke="#94A3B8" domain={[0, 1]} tick={{ fontSize: 9 }} width={32} />
                    <YAxis 
                      yAxisId="right" 
                      orientation="right" 
                      stroke="#F59E0B" 
                      domain={['dataMin - 0.5', 'dataMax + 0.5']}
                      tickFormatter={v => v.toFixed(1)}
                      tick={{ fontSize: 9 }}
                      width={28}
                      label={{ value: 'Line', angle: 90, position: 'insideRight', fill: '#F59E0B', fontSize: 9 }}
                    />
                    <Tooltip
                      {...chartTooltipWrapperProps}
                      content={makeChartTooltip((value, name) => {
                        if (name === 'Base Line') return [value?.toFixed(1), name];
                        return [(value * 100).toFixed(2) + '%', name];
                      }, {
                        chartAnchorRef: quoteChartRef,
                        contentStyle: { fontSize: '10px', padding: '4px 6px' },
                        itemStyle: { fontSize: '10px', margin: 0, padding: 0 },
                      })}
                    />
                    <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />
                    {showStartLine && (
                      <ReferenceLine 
                        x={fixtureStartTimeMs} 
                        stroke="#22C55E" 
                        strokeWidth={2} 
                        strokeDasharray="5 5"
                        label={{ value: 'Start', fill: '#22C55E', fontSize: 9, position: 'top' }}
                        ifOverflow="extendDomain"
                        yAxisId="left"
                      />
                    )}
                    <Line yAxisId="left" type="monotone" dataKey="p_fair" stroke={FAIR_PROB_LINE_COLOR} strokeWidth={2} name="P(Fair)" dot={false} connectNulls />
                    <Line yAxisId="left" type="monotone" dataKey="p_lower" stroke="#64748B" strokeWidth={1} strokeDasharray="5 5" name="Lower CI" dot={false} connectNulls />
                    <Line yAxisId="left" type="monotone" dataKey="p_upper" stroke="#64748B" strokeWidth={1} strokeDasharray="5 5" name="Upper CI" dot={false} connectNulls />
                    <Line yAxisId="right" type="stepAfter" dataKey="base_line" stroke="#F59E0B" strokeWidth={2} name="Base Line" dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
                </div>
              ) : (
                <div className="empty-state compact">No history. Click Refresh after updates.</div>
              )}
            </div>

            <div className="card chart-card chart-card-compact raw-prices-chart-row">
              <div className="chart-card-header">
                <h3>{priceChartType === 'p_fair' ? 'Demarginated Fair Probabilities' : 'Raw Prices'}</h3>
                {(availableLines.length > 0 || priceHistory.hasPfair) && (
                  <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                    {availableLines.length > 0 && (
                      <select className="chart-select chart-select-compact" value={selectedLine} onChange={e => handleLineChange(parseFloat(e.target.value))}>
                        {availableLines.map(l => <option key={l} value={l}>Line {l}</option>)}
                      </select>
                    )}
                    {priceHistory.hasPfair && (
                      <select 
                        className="chart-select chart-select-compact" 
                        value={priceChartType} 
                        onChange={e => setPriceChartType(e.target.value)}
                      >
                        <option value="price_over">Raw Prices</option>
                        <option value="p_fair">P Fair</option>
                      </select>
                    )}
                  </div>
                )}
              </div>
              {priceHistoryForChart.data && priceHistoryForChart.data.length > 0 && priceHistoryForChart.hasBookPrices ? (
                <div ref={rawPricesChartRef} className="raw-prices-chart-wrap chart-tooltip-anchor">
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={priceHistoryForChart.data} onClick={handleRawPricesChartClick} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(96,165,250,0.2)" />
                      <XAxis dataKey="time" type="number" scale="time" domain={['dataMin', 'dataMax']} stroke="#94A3B8" tickFormatter={formatAxisTime} tick={{ fontSize: 9 }} />
                      <YAxis stroke="#94A3B8" domain={priceChartType === 'p_fair' ? [0, 1] : ['auto', 'auto']} tick={{ fontSize: 9 }} width={36} />
                      <Tooltip
                        {...chartTooltipWrapperProps}
                        content={makeChartTooltip(
                          (value, name, props) => {
                            if (name === 'Marg. Price (Model)') return [value?.toFixed(2), name];
                            const suspendMatch = String(name).match(/^(.+)__suspend$/);
                            const book = suspendMatch ? suspendMatch[1] : name;
                            const isTwoSided = props.payload?.[`${book}_two_sided`];
                            const typeLabel = isTwoSided === true ? ' (2-sided)' : isTwoSided === false ? ' (1-sided)' : '';
                            const formattedValue = priceChartType === 'p_fair' ? (value * 100).toFixed(1) + '%' : value?.toFixed(2);
                            if (suspendMatch) {
                              return [`${formattedValue}${typeLabel} · suspended`, book];
                            }
                            return [formattedValue + typeLabel, name];
                          },
                          {
                            chartAnchorRef: rawPricesChartRef,
                            contentStyle: { fontSize: '10px', padding: '4px 6px' },
                            itemStyle: { fontSize: '10px', margin: 0, padding: 0 },
                          },
                        )}
                        cursor={{ stroke: 'rgba(96,165,250,0.4)', strokeWidth: 1 }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 10 }}
                        iconSize={8}
                        formatter={(value) => {
                        if (value === 'Marg. Price (Model)') return value;
                        if (String(value).endsWith(CHART_SUSPEND_OVERLAY_SUFFIX)) return null;
                        const isTwoSided = priceHistory.bookIsTwoSided?.[value];
                        return isTwoSided ? `${value} (2-sided)` : value;
                      }}
                      />
                      {showStartLine && (
                        <ReferenceLine 
                          x={fixtureStartTimeMs} 
                          stroke="#22C55E" 
                          strokeWidth={2} 
                          strokeDasharray="5 5"
                          label={{ value: 'Start', fill: '#22C55E', fontSize: 9, position: 'top' }}
                          ifOverflow="extendDomain"
                        />
                      )}
                      {spreadSnapshot?.point?.time != null && !spreadSnapshot.atLatest && (
                        <ReferenceLine
                          x={spreadSnapshot.point.time}
                          stroke="#fbbf24"
                          strokeWidth={1.5}
                          strokeDasharray="3 3"
                          ifOverflow="extendDomain"
                        />
                      )}
                      {priceHistory.books.map((book, idx) => {
                        const isTwoSided = priceHistory.bookIsTwoSided?.[book];
                        const color = BOOK_COLORS[book] || `hsl(${idx * 60},70%,60%)`;
                        const dataKeySuffix = priceChartType === 'p_fair' ? '_pfair' : '_over';
                        const suspendKey = priceChartType === 'p_fair' ? `${book}_suspendPfair` : `${book}_suspendOver`;
                        return (
                          <React.Fragment key={`${book}_${priceChartType}`}>
                            <Line
                              type="monotone"
                              dataKey={`${book}${dataKeySuffix}`}
                              stroke={color}
                              strokeWidth={isTwoSided ? 3 : 1.5}
                              name={book}
                              dot={false}
                              connectNulls={false}
                            />
                            <Line
                              type="linear"
                              dataKey={suspendKey}
                              stroke={color}
                              strokeWidth={1.5}
                              strokeOpacity={0.45}
                              strokeDasharray="5 4"
                              legendType="none"
                              name={`${book}${CHART_SUSPEND_OVERLAY_SUFFIX}`}
                              dot={false}
                              connectNulls={false}
                              isAnimationActive={false}
                            />
                          </React.Fragment>
                        );
                      })}
                      {priceChartType === 'price_over' && priceHistory.hasMarginated && (
                        <Line type="monotone" dataKey="marginated_price" stroke="#FF6B6B" strokeWidth={3} strokeDasharray="5 5" name="Marg. Price (Model)" dot={false} connectNulls />
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="empty-state compact">
                  {noCompetitorRawPrices
                    ? 'No competitor prices — no book lines for this market.'
                    : priceHistoryLineReady
                      ? 'No prices at this line. Try another line or click Refresh.'
                      : 'Loading lines…'}
                </div>
              )}
            </div>
          </div>

          {spreadSnapshot && spreadSnapshot.rows.length > 0 && (
            <div className="chart-section charts-two-col competitors-spread-row">
              <div className="card chart-card competitor-spread-card">
                <div className="chart-card-header">
                  <h3>Competitor pricing (two-sided)</h3>
                  <span style={{ color: '#94A3B8', fontSize: '0.85rem' }}>
                    {spreadSnapshot.point?.time
                      ? formatTime(spreadSnapshot.point.time)
                      : '—'}
                    {spreadSnapshot.atLatest ? ' · latest' : ''}
                  </span>
                </div>
                <p style={{ color: '#94A3B8', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
                  Bid/Ask = KF ref prices. Bars = implied P(over) on scrub chart line (Δ = translated).
                  <span style={{ color: '#67e8f9' }}> W%</span> = consensus weight ·
                  <span style={{ color: '#fbbf24' }}> p%</span> = demargin fair ·
                  <span style={{ color: '#64748b' }}> (vig)</span> = overround. Scrub picks time only.
                </p>
                <input
                  type="range"
                  min={0}
                  max={Math.max(0, spreadSnapshot.n - 1)}
                  value={spreadSnapshot.idx}
                  onChange={(e) => {
                    const i = Number(e.target.value);
                    const row = priceHistory?.data?.[i];
                    if (!row) return;
                    const ts = row.timestamp != null
                      ? Number(row.timestamp)
                      : (row.time != null ? Number(row.time) / 1000 : null);
                    if (ts == null || !Number.isFinite(ts)) return;
                    if (i >= (priceHistory?.data?.length || 0) - 1) setSpreadScrubTs(null);
                    else setSpreadScrubTs(ts);
                  }}
                  style={{ width: '100%', marginBottom: '0.75rem' }}
                />
                <div className="competitor-spread-grid competitor-spread-grid--header">
                  <span className="competitor-spread-col-book">Book</span>
                  <span className="competitor-spread-col-weight" title="Consensus weight share (1/R)">W%</span>
                  <div className="competitor-spread-col-bar competitor-spread-axis">
                    <span style={{ position: 'absolute', left: 0 }}>0%</span>
                    <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)' }}>50%</span>
                    <span style={{ position: 'absolute', right: 0 }}>100%</span>
                  </div>
                  <span className="competitor-spread-col-bid">Bid</span>
                  <span className="competitor-spread-col-line">Line · p</span>
                  <span className="competitor-spread-col-ask">Ask</span>
                </div>
                <div className="competitor-spread-rows">
                  {spreadSnapshot.rows.map((r, rowIdx) => {
                    const bidPct = r.bid != null ? Math.min(100, Math.max(0, r.bid * 100)) : null;
                    const askPct = r.ask != null ? Math.min(100, Math.max(0, r.ask * 100)) : null;
                    const fairPct =
                      r.bookFair != null
                        ? Math.min(100, Math.max(0, r.bookFair * 100))
                        : null;
                    let barLo = null;
                    let barHi = null;
                    if (bidPct != null && askPct != null) {
                      barLo = Math.min(bidPct, askPct);
                      barHi = Math.max(bidPct, askPct);
                    } else if (askPct != null && fairPct != null) {
                      barLo = Math.min(askPct, fairPct);
                      barHi = Math.max(askPct, fairPct);
                    } else if (bidPct != null && fairPct != null) {
                      barLo = Math.min(bidPct, fairPct);
                      barHi = Math.max(bidPct, fairPct);
                    }
                    const width = barLo != null && barHi != null ? Math.max(barHi - barLo, 0.4) : 0;
                    const rowAccent = r.isOurs ? '#f472b6' : null;
                    const bidFaded = r.isOurs && r.bidQuoted === false && r.bid != null;
                    const askFaded = r.isOurs && r.askQuoted === false && r.ask != null;
                    const bidImp = r.priceUnder != null && Number(r.priceUnder) > 0
                      ? (100 / Number(r.priceUnder))
                      : null;
                    const askImp = r.priceOver != null && Number(r.priceOver) > 0
                      ? (100 / Number(r.priceOver))
                      : null;
                    const bidTitle = r.priceUnder != null
                      ? `${Number(r.priceUnder).toFixed(2)}${bidImp != null ? ` (${bidImp.toFixed(1)}% implied under)` : ''}${bidFaded ? ' · not quoting' : ''}`
                      : null;
                    const askTitle = r.priceOver != null
                      ? `${Number(r.priceOver).toFixed(2)}${askImp != null ? ` (${askImp.toFixed(1)}% implied over)` : ''}${askFaded ? ' · not quoting' : ''}`
                      : null;
                    const lineLabel = r.sourceLine != null && Number.isFinite(Number(r.sourceLine))
                      ? Number(r.sourceLine)
                      : '—';
                    const weightNum = r.weightFrac != null && Number.isFinite(r.weightFrac)
                      ? r.weightFrac * 100
                      : null;
                    const weightPct = weightNum != null ? weightNum.toFixed(0) : null;
                    const vigNum = r.vig != null && Number.isFinite(r.vig) ? r.vig * 100 : null;
                    const vigPct = vigNum != null ? vigNum.toFixed(1) : null;
                    const bookTitle = [
                      r.translated ? 'Bar translated from ref line onto chart line; Bid/Ask stay on ref' : null,
                      weightNum != null
                        ? `Consensus weight ${weightNum.toFixed(1)}% of Σ(1/R) across books shown`
                        : null,
                    ].filter(Boolean).join(' · ') || undefined;
                    return (
                      <div
                        key={`${r.book}-${rowIdx}`}
                        className="competitor-spread-grid competitor-spread-grid--row"
                      >
                        <span
                          className="competitor-spread-col-book"
                          style={{
                            color: rowAccent || '#E2E8F0',
                            fontWeight: r.isOurs ? 600 : 400,
                          }}
                          title={bookTitle}
                        >
                          <span className="competitor-spread-book-name">{r.book}</span>
                          <span
                            className={`competitor-spread-side-chip ${r.twoSided ? 'competitor-spread-side-chip--2w' : 'competitor-spread-side-chip--1w'}`}
                          >
                            {r.twoSided ? '2w' : '1w'}
                          </span>
                          {r.translated ? <span className="competitor-spread-delta-chip">Δ</span> : null}
                          {fmtLiq(r.sizeOver) || fmtLiq(r.sizeUnder) ? (
                            <span
                              className="competitor-spread-liq-chip"
                              title={`Top-of-book size — over ${fmtLiq(r.sizeOver) ?? '—'} / under ${fmtLiq(r.sizeUnder) ?? '—'}`}
                            >
                              {fmtLiq(r.sizeOver) ?? '—'}/{fmtLiq(r.sizeUnder) ?? '—'}
                            </span>
                          ) : null}
                        </span>
                        <span
                          className="competitor-spread-col-weight"
                          title={weightNum != null ? `Weight ${weightNum.toFixed(1)}% of Σ(1/R)` : 'No weight'}
                        >
                          {weightPct != null ? (
                            <>
                              <span className="competitor-spread-weight-val">{weightPct}%</span>
                              <span className="competitor-spread-weight-track" aria-hidden>
                                <span
                                  className="competitor-spread-weight-fill"
                                  style={{ width: `${Math.min(100, Math.max(4, weightNum))}%` }}
                                />
                              </span>
                            </>
                          ) : (
                            <span className="competitor-spread-weight-empty">—</span>
                          )}
                        </span>
                        <div
                          className={`competitor-spread-col-bar competitor-spread-bar${r.isOurs ? ' competitor-spread-bar--ours' : ''}`}
                          title={[
                            bidTitle ? `bid ${bidTitle}` : null,
                            askTitle ? `ask ${askTitle}` : null,
                            fairPct != null ? `book p_fair ${fairPct.toFixed(1)}%` : null,
                          ].filter(Boolean).join(' · ')}
                        >
                          <div className="competitor-spread-bar-mid" />
                          {barLo != null && barHi != null && (
                            <div
                              className="competitor-spread-bar-range"
                              style={{
                                left: `${barLo}%`,
                                width: `${width}%`,
                                borderLeftColor: bidPct != null
                                  ? (rowAccent || '#34d399')
                                  : '#fbbf24',
                                borderRightColor: askPct != null
                                  ? (r.isOurs ? '#fb7185' : '#60a5fa')
                                  : '#fbbf24',
                                opacity: (bidFaded || askFaded) ? 0.55 : 1,
                              }}
                            />
                          )}
                          {bidPct != null && askPct == null && fairPct == null && (
                            <div
                              className="competitor-spread-bar-tick"
                              style={{
                                left: `calc(${bidPct}% - 2px)`,
                                background: rowAccent || '#34d399',
                                opacity: bidFaded ? 0.35 : 1,
                              }}
                            />
                          )}
                          {askPct != null && bidPct == null && fairPct == null && (
                            <div
                              className="competitor-spread-bar-tick"
                              style={{
                                left: `calc(${askPct}% - 2px)`,
                                background: r.isOurs ? '#fb7185' : '#60a5fa',
                                opacity: askFaded ? 0.35 : 1,
                              }}
                            />
                          )}
                          {fairPct != null && (
                            <div
                              className="competitor-spread-bar-fair"
                              style={{ left: `calc(${fairPct}% - 1px)` }}
                            />
                          )}
                        </div>
                        <span
                          className={`competitor-spread-col-bid competitor-spread-leg--bid${bidFaded ? ' competitor-spread-leg--faded' : ''}`}
                          title={bidFaded ? 'Not quoting under' : undefined}
                        >
                          {formatSpreadLegCell(r.priceUnder)}
                        </span>
                        <span className="competitor-spread-col-line">
                          <span className="competitor-spread-line-main">{lineLabel}</span>
                          {r.bookFairSource != null && Number.isFinite(Number(r.bookFairSource)) ? (
                            <span
                              className="competitor-spread-line-fair"
                              title="Demargin p_fair on ref line (over)"
                            >
                              {(Number(r.bookFairSource) * 100).toFixed(1)}%
                            </span>
                          ) : null}
                          {vigPct != null ? (
                            <span
                              className="competitor-spread-line-vig"
                              title={`Implied vig (overround) ${vigPct}%`}
                            >
                              {`(${vigPct}%)`}
                            </span>
                          ) : null}
                        </span>
                        <span
                          className={`competitor-spread-col-ask competitor-spread-leg--ask${askFaded ? ' competitor-spread-leg--faded' : ''}`}
                          title={askFaded ? 'Not quoting over' : undefined}
                        >
                          {formatSpreadLegCell(r.priceOver)}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="competitor-spread-legend">
                  <span><span style={{ color: '#34d399' }}>■</span> Bid (under)</span>
                  <span><span style={{ color: '#60a5fa' }}>■</span> Ask (over)</span>
                  <span><span style={{ color: '#f472b6' }}>■</span> Sleeper quote</span>
                  <span><span style={{ color: '#fbbf24' }}>│</span> p_fair</span>
                  <span><span style={{ color: '#67e8f9' }}>■</span> W% weight</span>
                  <span style={{ color: '#64748b' }}>(vig)</span>
                </div>
              </div>
              <div className="card chart-card competitor-updates-tiny">
                <h3>Recent updates</h3>
                {updatesHistory.length > 0 ? (
                  <div className="scrollable-table-container competitor-updates-tiny-scroll">
                    <table className="competitor-updates-tiny-table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Type</th>
                          <th>Detail</th>
                          <th>N</th>
                        </tr>
                      </thead>
                      <tbody>
                        {updatesHistory.slice(0, 40).map((row, i) => (
                          <tr key={i} title={row.update_detail || undefined}>
                            <td>{formatTime(new Date((row.timestamp || 0) * 1000))}</td>
                            <td>{row.update_type || '—'}</td>
                            <td>{row.update_detail || '—'}</td>
                            <td>{row.n_books != null ? row.n_books : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state compact">No updates.</div>
                )}
              </div>
            </div>
          )}

          {!(spreadSnapshot && spreadSnapshot.rows.length > 0) && (
            <div className="chart-section charts-two-col competitors-spread-row">
              <div className="card chart-card competitor-updates-tiny competitor-updates-tiny--solo">
                <h3>Recent updates</h3>
                {updatesHistory.length > 0 ? (
                  <div className="scrollable-table-container competitor-updates-tiny-scroll">
                    <table className="competitor-updates-tiny-table">
                      <thead>
                        <tr>
                          <th>Time</th>
                          <th>Type</th>
                          <th>Detail</th>
                          <th>N</th>
                        </tr>
                      </thead>
                      <tbody>
                        {updatesHistory.slice(0, 40).map((row, i) => (
                          <tr key={i} title={row.update_detail || undefined}>
                            <td>{formatTime(new Date((row.timestamp || 0) * 1000))}</td>
                            <td>{row.update_type || '—'}</td>
                            <td>{row.update_detail || '—'}</td>
                            <td>{row.n_books != null ? row.n_books : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state compact">No updates.</div>
                )}
              </div>
            </div>
          )}

          <div className="chart-section charts-two-col competitors-bottom-charts">
            <div className="card chart-card">
              <h3>Competitor signals</h3>
              <p className="competitors-chart-lede">
                One row per ingest batch. Fuse lists movers; dropout / lock drops show
                Entries P impact; per-book collapses discovery / jump / vig when enabled.
              </p>
              {competitorSignalRows.length > 0 ? (
                <div className="scrollable-table-container competitor-signals-scroll">
                  <table className="competitor-signals-table">
                    <thead>
                      <tr>
                        <th className="col-time">Time</th>
                        <th>Event</th>
                        <th title="Books that moved in this batch">Books</th>
                        <th
                          title={
                            isUncertaintyV2
                              ? 'Δx applied to our price (sharp offsets kept)'
                              : 'Shared Δmid when uniform across the batch'
                          }
                        >
                          {isUncertaintyV2 ? 'Δx' : 'Δmid'}
                        </th>
                        <th
                          title={
                            isUncertaintyV2
                              ? 'Share of a sharp offset the move absorbed: γΔ²/(P_panel+γΔ²)'
                              : undefined
                          }
                        >
                          {isUncertaintyV2 ? 'K' : 'R'}
                        </th>
                        <th title="Entries posterior after batch">p</th>
                        <th title="Entries P after batch (Δ below)">P</th>
                      </tr>
                    </thead>
                    <tbody>
                      {competitorSignalRows.map((row) => (
                        <tr key={row.id} title={row.booksTitle || row.eventTitle}>
                          <td className="col-time">{formatCompetitorTime(row.timestamp)}</td>
                          <td
                            style={{ color: competitorEventColor(row.raw?.[0]?.event) }}
                            title={row.eventTitle}
                          >
                            {row.eventLabel}
                            {row.bookCount > 1 ? (
                              <span className="competitor-signals-batch-n">×{row.bookCount}</span>
                            ) : null}
                          </td>
                          <td title={row.booksTitle}>
                            <span className="competitor-signals-books">{row.booksLabel || '—'}</span>
                          </td>
                          <td title={row.deltaTitle}>
                            {row.delta_mid != null ? Number(row.delta_mid).toFixed(3) : (row.deltaTitle ? '…' : '—')}
                          </td>
                          <td>
                            {isUncertaintyV2
                              ? (row.K != null ? Number(row.K).toFixed(3) : '—')
                              : (row.R_obs != null ? Number(row.R_obs).toFixed(3) : '—')}
                          </td>
                          <td>{formatEntriesPMarket(row)}</td>
                          <td>{formatCompetitorDeltaP(row)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state compact">
                  No competitor signal rows. Writers emit fuse rows on book moves, or per-book discovery/jump/vig when enabled.
                </div>
              )}
            </div>

            <div className="card chart-card diagnostics-chart-section">
              <div className="chart-card-header">
                <h3>Diagnostics</h3>
                <select
                  className="chart-select chart-select-compact"
                  value={selectedDiagnosticBottom}
                  onChange={(e) => setSelectedDiagnosticBottom(e.target.value)}
                >
                  {DIAGNOSTIC_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{diagnosticLabel(t.value, isUncertaintyV2)}</option>
                  ))}
                </select>
              </div>
              {diagnosticsBottomChartData.length > 0 ? (
                <div ref={diagnosticsChartRef} className="chart-tooltip-anchor">
                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={diagnosticsBottomChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(96,165,250,0.2)" />
                      <XAxis
                        dataKey="time"
                        type="number"
                        scale="time"
                        domain={['dataMin', 'dataMax']}
                        stroke="#94A3B8"
                        tickFormatter={formatAxisTime}
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis
                        stroke={selectedDiagnosticBottom === 'entries_p_variance' ? '#A78BFA' : '#F59E0B'}
                        tick={{ fontSize: 10 }}
                        width={42}
                        domain={
                          selectedDiagnosticBottom === 'gating'
                            ? [0, 1]
                            : selectedDiagnosticBottom === 'n_books'
                              ? [0, 'auto']
                              : undefined
                        }
                        ticks={selectedDiagnosticBottom === 'gating' ? [0, 1] : undefined}
                        tickFormatter={
                          selectedDiagnosticBottom === 'gating'
                            ? (v) => (v === 1 ? 'OK' : 'Blk')
                            : undefined
                        }
                        allowDecimals={selectedDiagnosticBottom !== 'n_books'}
                      />
                      <Tooltip
                        {...chartTooltipWrapperProps}
                        content={makeChartTooltip((v, name, entry) => {
                          const row = entry?.payload || {};
                          if (selectedDiagnosticBottom === 'entries_p_variance') {
                            const parts = [Number(v).toFixed(4)];
                            if (row.sportsbook && row.event) {
                              parts.push(`${row.sportsbook} · ${shortCompetitorEventDescription(row.event)}`);
                            } else if (row.update_type) {
                              parts.push(String(row.update_type).replace(/_/g, ' '));
                            }
                            if (row.p_variance_delta != null && Math.abs(Number(row.p_variance_delta)) > 1e-6) {
                              const dP = Number(row.p_variance_delta);
                              parts.push(`ΔP ${dP > 0 ? '+' : '−'}${Math.abs(dP).toFixed(4)}`);
                            }
                            if (row.z2 != null) parts.push(`z²=${Number(row.z2).toFixed(3)}`);
                            return [parts.join(' · '), 'EntriesKF P'];
                          }
                          if (selectedDiagnosticBottom === 'gating') {
                            const reason = row.gate_reason || row.update_detail;
                            const label = Number(v) === 1 ? 'Allowed' : 'Blocked';
                            return [reason ? `${label} · ${reason}` : label, 'Gate'];
                          }
                          const label =
                            diagnosticLabel(selectedDiagnosticBottom, isUncertaintyV2) || name;
                          const detail = row.update_type || row.update_detail;
                          const val = v != null && Number.isFinite(Number(v)) ? String(v) : '—';
                          return [detail ? `${val} · ${detail}` : val, label];
                        }, { chartAnchorRef: diagnosticsChartRef })}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {showStartLine && (
                        <ReferenceLine
                          x={fixtureStartTimeMs}
                          stroke="#22C55E"
                          strokeWidth={1}
                          strokeDasharray="5 5"
                          ifOverflow="extendDomain"
                        />
                      )}
                      <Line
                        type={
                          selectedDiagnosticBottom === 'n_books' || selectedDiagnosticBottom === 'gating'
                            ? 'stepAfter'
                            : 'monotone'
                        }
                        dataKey="value"
                        stroke={selectedDiagnosticBottom === 'entries_p_variance' ? '#A78BFA' : '#F59E0B'}
                        strokeWidth={2}
                        name={diagnosticLabel(selectedDiagnosticBottom, isUncertaintyV2)}
                        dot={selectedDiagnosticBottom === 'n_books'}
                        connectNulls
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="empty-state compact">No diagnostics. Click Refresh.</div>
              )}
            </div>
          </div>
          </>
          )}

          {activeTab === 'risk' && (
          <>
          <section className="current-quote-row">
            <div className="manual-intervention-ob-row">
              <div className="card quote-card">
                <h3>Gate & actions</h3>
                {selectedMarket && (
                  <ManualGatingPanel marketKey={selectedMarket} quote={currentQuote} />
                )}
              </div>
              <GateBreakdownPanel quote={currentQuote} />
            </div>
          </section>

          {(latestLadderSnapshot || riskLiabilityBreakdown) && (
            <section className="chart-section">
              <div className="risk-top-row">
                {latestLadderSnapshot && (
                  <div className="card chart-card risk-section-card risk-ladder-card">
                    <h3 style={{ marginTop: 0, marginBottom: '0.35rem' }}>Ladder pricing</h3>
                    <p className="risk-card-lede risk-card-lede--compact">
                      Latest {formatTime(new Date(latestLadderSnapshot.latestTs * 1000))} · base{' '}
                      {latestLadderSnapshot.rows[0]?.base_line ?? '—'} · fair{' '}
                      {latestLadderSnapshot.rows[0]?.p_fair_base != null
                        ? Number(latestLadderSnapshot.rows[0].p_fair_base).toFixed(3)
                        : '—'}
                      . <span className="risk-lede-accent">Accent bar</span> = baseline line. Δp quote =
                      (1/Over) − P(over). N / fees / price wgt / liab roll up <strong>by line only</strong>{' '}
                      (all P% buckets). Exposure by line keeps line × P(over)% detail.
                    </p>
                    <div className="scrollable-table-container compact">
                      <table
                        className="risk-ladder-table"
                        style={{ width: '100%', borderCollapse: 'collapse', color: '#E2E8F0', fontSize: '0.82rem' }}
                      >
                        <thead>
                          <tr style={{ borderBottom: '1px solid #334155' }}>
                            <th style={{ textAlign: 'left', padding: '0.45rem' }} title="Alt line (ladder_quotes.line)">
                              Line
                            </th>
                            <th style={{ textAlign: 'right', padding: '0.45rem' }}>P(over)</th>
                            <th
                              style={{ textAlign: 'right', padding: '0.45rem' }}
                              title="Marginated quote decimals from ladder: over price (O) and under price (U) at this line."
                            >
                              Over / Under
                            </th>
                            <th
                              style={{ textAlign: 'right', padding: '0.45rem' }}
                              title="(1 / Over decimal) − P(over): implied probability at our quoted over price minus fair P(over)"
                            >
                              Δp quote
                            </th>
                            <th
                              style={{ textAlign: 'right', padding: '0.45rem' }}
                              title="Entry leg count at this line (all true P(over)% buckets combined)"
                            >
                              N
                            </th>
                            <th
                              style={{ textAlign: 'right', padding: '0.45rem' }}
                              title="Sum of fees at this line (all P% buckets); shown as total (O over-fees / U under-fees)"
                            >
                              Fees (O / U)
                            </th>
                            <th
                              style={{ textAlign: 'right', padding: '0.45rem' }}
                              title="Fee-weighted mean posted decimal across all legs at this line, then (O over-only mean / U under-only mean). Leading = combined fee-weighted decimal."
                            >
                              Price wgt (O / U)
                            </th>
                            <th
                              style={{ textAlign: 'right', padding: '0.45rem' }}
                              title="Leading = max(O-side net, U-side net) at this line; O / U = net if over vs under resolves (same formulas as former Liab O / U columns)."
                            >
                              Liab (O / U)
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {latestLadderSnapshot.rows.map((row, i) => {
                            const bl = effectiveBaselineLine;
                            const isBaselineRow =
                              bl != null && row.line != null && lineClose(Number(row.line), bl);
                            const lineStat = lookupEntryStatLineOnly(row.line, entryStatsAggregatedByLine);
                            const margQuote = ladderQuoteMarginationProb(row);
                            return (
                              <tr
                                key={`ladder-${row.line}-${i}`}
                                className={isBaselineRow ? 'risk-baseline-row' : undefined}
                                style={{ borderBottom: '1px solid #334155' }}
                              >
                                <td style={{ padding: '0.45rem' }}>{row.line}</td>
                                <td style={{ textAlign: 'right', padding: '0.45rem' }}>
                                  {row.p_over != null ? row.p_over.toFixed(3) : '—'}
                                </td>
                                <td
                                  style={{ textAlign: 'right', padding: '0.45rem' }}
                                  title="Quoted marginated decimals: O = over, U = under at this ladder line"
                                >
                                  {renderLadderMarginatedOverUnder(row)}
                                </td>
                                <td style={{ textAlign: 'right', padding: '0.45rem' }}>
                                  {margQuote != null ? margQuote.toFixed(4) : '—'}
                                </td>
                                <td style={{ textAlign: 'right', padding: '0.45rem' }}>
                                  {lineStat?.n_entries != null ? lineStat.n_entries : '—'}
                                </td>
                                <td
                                  style={{ textAlign: 'right', padding: '0.45rem' }}
                                  title="O / U = fee mass on over vs under legs at this line (all P% buckets)"
                                >
                                  {lineStat != null ? renderFeesWithOverUnder(lineStat) : '—'}
                                </td>
                                <td
                                  style={{ textAlign: 'right', padding: '0.45rem' }}
                                  title="Combined fee-weighted decimal; O / U = side-specific fee-weighted means at this line"
                                >
                                  {lineStat != null ? renderPriceWgtWithOverUnder(lineStat) : '—'}
                                </td>
                                <td
                                  style={{ textAlign: 'right', padding: '0.45rem' }}
                                  title="max(O net, U net); O / U = per-side net at this line"
                                >
                                  {lineStat != null ? renderLiabilityWithOverUnder(lineStat) : '—'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {riskLiabilityBreakdown && (
                  <div className="card chart-card risk-section-card risk-liability-card">
                    <h3 style={{ marginTop: 0, marginBottom: '0.35rem' }}>Liability breakdown</h3>
                    <p className="risk-card-lede risk-card-lede--compact">
                      Entry totals for baseline
                      {riskLiabilityBreakdown.baselineLine != null
                        ? ` ${riskLiabilityBreakdown.baselineLine.toFixed(2)}`
                        : ''}
                      . Other lines: ladder + exposure table.
                    </p>
                    <div className="risk-liability-grid">
                      <div><span>Entries</span><strong>{riskLiabilityBreakdown.nEntries}</strong></div>
                      <div>
                        <span>Total fees</span>
                        <strong>
                          {renderFeesWithOverUnder({
                            total_fees: riskLiabilityBreakdown.totalFees,
                            total_fees_over: riskLiabilityBreakdown.totalFeesOver,
                            total_fees_under: riskLiabilityBreakdown.totalFeesUnder,
                          })}
                        </strong>
                      </div>
                      <div><span>Avg sharp</span><strong>{riskLiabilityBreakdown.avgSharp != null ? riskLiabilityBreakdown.avgSharp.toFixed(2) : '—'}</strong></div>
                      <div><span>Liability (over)</span><strong>{riskLiabilityBreakdown.over != null ? riskLiabilityBreakdown.over.toFixed(2) : '—'}</strong></div>
                      <div><span>Liability (under)</span><strong>{riskLiabilityBreakdown.under != null ? riskLiabilityBreakdown.under.toFixed(2) : '—'}</strong></div>
                      <div>
                        <span>Worst-case liab.</span>
                        <strong>
                          {riskLiabilityBreakdown.worstCaseLiability != null ? (
                            riskLiabilityBreakdown.sideAtRisk ? (
                              <span className="risk-metric-inline">
                                <span className="risk-ou-split">{riskLiabilityBreakdown.sideAtRisk}</span>
                                <span className="risk-metric-primary">
                                  {riskLiabilityBreakdown.worstCaseLiability.toFixed(2)}
                                </span>
                              </span>
                            ) : (
                              <span className="risk-metric-primary">
                                {riskLiabilityBreakdown.worstCaseLiability.toFixed(2)}
                              </span>
                            )
                          ) : (
                            '—'
                          )}
                        </strong>
                      </div>
                      <div><span>Pre-game handle cap</span><strong>{riskLiabilityBreakdown.handleLimit != null ? formatPreGameHandleLimitUsd(riskLiabilityBreakdown.handleLimit) : '—'}</strong></div>
                      <div><span>Cap utilization</span><strong>{riskLiabilityBreakdown.utilization != null ? `${(riskLiabilityBreakdown.utilization * 100).toFixed(1)}%` : '—'}</strong></div>
                    </div>
                  </div>
                )}
                </div>
            </section>
          )}

          {selectedMarket && (
            <section className="chart-section">
              <div className="card chart-card full-width risk-exposure-by-line-card">
                <h3 style={{ marginTop: 0, marginBottom: '0.35rem' }}>Exposure by line</h3>
                <p className="risk-card-lede risk-card-lede--compact">
                  One row per leg line × true P(over) (% rounded to whole number from entry payload).{' '}
                  <span className="risk-lede-accent">Accent bar</span> = baseline line. Hover headers for formulas.
                </p>
                {entryStatsByLineRows.length > 0 ? (
                  <div className="scrollable-table-container risk-exposure-by-line-scroll">
                    <table
                      className="risk-ladder-table"
                      style={{ width: '100%', borderCollapse: 'collapse', color: '#E2E8F0', fontSize: '0.85rem' }}
                    >
                      <thead>
                        <tr style={{ borderBottom: '1px solid #334155' }}>
                          <th style={{ textAlign: 'left', padding: '0.5rem' }} title="Leg line (outcome_value)">
                            Line
                          </th>
                          <th
                            style={{ textAlign: 'right', padding: '0.5rem' }}
                            title="True P(over) at leg line: entry probability (over) or 1−probability (under), ×100 rounded to integer"
                          >
                            P(over) %
                          </th>
                          <th style={{ textAlign: 'right', padding: '0.5rem' }} title="Row count for this line × P(over)% bucket">
                            Entries
                          </th>
                          <th
                            style={{ textAlign: 'right', padding: '0.5rem' }}
                            title="Sum of currency_amount; shown as total (O over-fees / U under-fees)"
                          >
                            Fees (O / U)
                          </th>
                          <th
                            style={{ textAlign: 'right', padding: '0.5rem' }}
                            title="Fee-weighted avg posted decimal — over legs only"
                          >
                            Price wgt (O)
                          </th>
                          <th
                            style={{ textAlign: 'right', padding: '0.5rem' }}
                            title="Fee-weighted avg posted decimal — under legs only"
                          >
                            Price wgt (U)
                          </th>
                          <th style={{ textAlign: 'right', padding: '0.5rem' }} title="Pending legs, sharp_score">
                            Avg sharp
                          </th>
                          <th
                            style={{ textAlign: 'right', padding: '0.5rem' }}
                            title="Sum(liability_contrib over) − Sum(fees under)"
                          >
                            Liab (over)
                          </th>
                          <th
                            style={{ textAlign: 'right', padding: '0.5rem' }}
                            title="Sum(liability_contrib under) − Sum(fees over)"
                          >
                            Liab (under)
                          </th>
                          <th style={{ textAlign: 'right', padding: '0.5rem' }} title="max(over, under)">
                            Worst
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {entryStatsByLineRows.map((r) => {
                          const bl = effectiveBaselineLine;
                          const isBl =
                            bl != null && r.line != null && lineClose(Number(r.line), Number(bl));
                          const o = r.liability_net_over;
                          const u = r.liability_net_under;
                          const worst =
                            o != null && u != null ? Math.max(Number(o), Number(u)) : o != null ? Number(o) : u != null ? Number(u) : null;
                          const pk =
                            r.line != null && r.true_p_over_pct != null
                              ? `${Number(r.line)}-${Number(r.true_p_over_pct)}`
                              : String(r.line);
                          return (
                            <tr
                              key={pk}
                              className={isBl ? 'risk-baseline-row' : undefined}
                              style={{ borderBottom: '1px solid #334155' }}
                            >
                              <td style={{ padding: '0.5rem' }}>{r.line != null ? Number(r.line).toFixed(2) : '—'}</td>
                              <td style={{ textAlign: 'right', padding: '0.5rem' }}>
                                {r.true_p_over_pct != null ? `${Number(r.true_p_over_pct)}%` : '—'}
                              </td>
                              <td style={{ textAlign: 'right', padding: '0.5rem' }}>{r.n_entries ?? 0}</td>
                              <td style={{ textAlign: 'right', padding: '0.5rem' }} title="Per line × P(over)% bucket">
                                {renderFeesWithOverUnder(r)}
                              </td>
                              <td style={{ textAlign: 'right', padding: '0.5rem' }}>
                                {r.weighted_avg_decimal_over != null &&
                                Number.isFinite(Number(r.weighted_avg_decimal_over))
                                  ? Number(r.weighted_avg_decimal_over).toFixed(2)
                                  : '—'}
                              </td>
                              <td style={{ textAlign: 'right', padding: '0.5rem' }}>
                                {r.weighted_avg_decimal_under != null &&
                                Number.isFinite(Number(r.weighted_avg_decimal_under))
                                  ? Number(r.weighted_avg_decimal_under).toFixed(2)
                                  : '—'}
                              </td>
                              <td style={{ textAlign: 'right', padding: '0.5rem' }}>
                                {r.avg_sharp_score != null ? Number(r.avg_sharp_score).toFixed(2) : '—'}
                              </td>
                              <td style={{ textAlign: 'right', padding: '0.5rem' }}>
                                {r.liability_net_over != null ? Number(r.liability_net_over).toFixed(2) : '—'}
                              </td>
                              <td style={{ textAlign: 'right', padding: '0.5rem' }}>
                                {r.liability_net_under != null ? Number(r.liability_net_under).toFixed(2) : '—'}
                              </td>
                              <td style={{ textAlign: 'right', padding: '0.5rem' }}>
                                {worst != null && Number.isFinite(worst) ? worst.toFixed(2) : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state compact">No line × P(over)% entry rows for this market yet.</div>
                )}
              </div>
            </section>
          )}

          {/* Recent entries feed */}
          <section className="chart-section">
            <div className="card chart-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <h3 style={{ margin: 0 }}>Recent entries</h3>
                {selectedMarket && (
                  <Link
                    to={{ pathname: '/dashboard', search: `?market=${encodeURIComponent(selectedMarket)}` }}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="button"
                    style={{ fontSize: '0.8rem', padding: '0.35rem 0.6rem', textDecoration: 'none', color: 'inherit' }}
                  >
                    Open dashboard in new tab
                  </Link>
                )}
              </div>
              {currentQuote && (
                <div className="entries-market-context" style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem', background: 'rgba(30,41,59,0.6)', borderRadius: 6, fontSize: '0.85rem', color: '#94A3B8' }}>
                  <strong style={{ color: '#E2E8F0' }}>Market:</strong> {currentQuote.player_name || '—'} · {currentQuote.market_id || '—'} line {currentQuote.line != null ? currentQuote.line : '—'}
                  {fixtureStartTimeMs && (
                    <> · <strong style={{ color: '#E2E8F0' }}>Game (local):</strong> {formatTime(fixtureStartTimeMs)}</>
                  )}
                  {currentQuote.league && <> · {currentQuote.league}</>}
                </div>
              )}
              <div className="entries-feed-filters" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  Min sharp
                  <input
                    type="number"
                    min={0}
                    max={1}
                    step={0.1}
                    placeholder="—"
                    value={entriesMinSharp}
                    onChange={e => setEntriesMinSharp(e.target.value)}
                    style={{ width: '4rem', padding: '0.25rem 0.5rem' }}
                  />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  Min fee
                  <input
                    type="number"
                    min={0}
                    step={5}
                    placeholder="—"
                    value={entriesMinFee}
                    onChange={e => setEntriesMinFee(e.target.value)}
                    style={{ width: '5rem', padding: '0.25rem 0.5rem' }}
                  />
                </label>
              </div>
              {Array.isArray(marketEntries) && marketEntries.length > 0 ? (
                <div className="table-container" style={{ maxHeight: '280px', overflow: 'auto' }}>
                  <table className="table compact-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>User</th>
                        <th>Fee</th>
                        <th title="Total legs on the entry (1 = straight, 2+ = parlay)">Num legs</th>
                        <th>Sharp (raw)</th>
                        <th>Sharp (adj)</th>
                        <th>Repl</th>
                        <th title="Leg decimal odds">Leg ×</th>
                        <th title="Full parlay decimal odds">Entry ×</th>
                        <th title="Per-leg liability component">Liab.</th>
                        <th>Prob</th>
                        <th>Outcome</th>
                        <th>Line</th>
                        <th>Line type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marketEntries.map((e, i) => {
                        const promoClass = entryPromoRowClass(e);
                        const replBadge = formatReplicationBadge(e);
                        return (
                        <tr key={e.parlay_id + (e.created_ts ?? i)} className={promoClass || undefined}>
                          <td title={e.created_ts != null ? new Date(e.created_ts * 1000).toLocaleString() : ''}>
                            {e.created_ts != null ? new Date(e.created_ts * 1000).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                          </td>
                          <td title={e.user_id || ''} style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.user_id || '—'}</td>
                          <td>{e.currency_amount != null ? '$' + Number(e.currency_amount).toFixed(0) : '—'}</td>
                          <td>{e.num_legs != null ? e.num_legs : '—'}</td>
                          <td style={sharpCellStyle(e.sharp_score)}>{e.sharp_score != null ? Number(e.sharp_score).toFixed(2) : '—'}</td>
                          <td style={sharpCellStyle(e.sharp_score_adjusted)}>
                            {e.sharp_score_adjusted != null ? Number(e.sharp_score_adjusted).toFixed(2) : '—'}
                          </td>
                          <td>{replBadge || '—'}</td>
                          <td>{e.payout_multiplier != null ? Number(e.payout_multiplier).toFixed(2) + '×' : '—'}</td>
                          <td>{e.entry_multiplier != null ? Number(e.entry_multiplier).toFixed(2) + '×' : '—'}</td>
                          <td>{e.liability_contrib != null ? Number(e.liability_contrib).toFixed(2) : '—'}</td>
                          <td>{e.probability != null ? (Number(e.probability) * 100).toFixed(1) + '%' : '—'}</td>
                          <td>{e.outcome || '—'}</td>
                          <td>{e.outcome_value != null ? Number(e.outcome_value) : '—'}</td>
                          <td title={e.line_type || ''}>{e.line_type || '—'}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state compact">No entries for this market (or filters too strict).</div>
              )}
            </div>
          </section>

          {/* Gating chart */}
          <section className="chart-section">
            <div className="card chart-card full-width risk-section-card">
              <h3 style={{ marginTop: 0, marginBottom: '0.35rem' }}>Gating & posterior P (variance)</h3>
              <p className="risk-card-lede risk-card-lede--compact">
                Gate from <code>kf_diagnostics</code>. Posterior variance uses EntriesKF diagnostics (<code>entries_p_variance</code>) when <code>quote_source</code> is entries; otherwise base <code>P_variance_post</code>. Innovation z² reflects per-book competitor signals when <code>competitor_per_book_signals</code> is enabled on the writer.
              </p>
              {riskGateVarianceHistoryForChart.length > 0 ? (
                <div ref={riskGateVarianceChartRef} className="chart-tooltip-anchor">
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={riskGateVarianceHistoryForChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(96,165,250,0.2)" />
                    <XAxis dataKey="time" type="number" scale="time" domain={['dataMin', 'dataMax']} stroke="#94A3B8" tickFormatter={formatAxisTime} tick={{ fontSize: 11 }} />
                    <YAxis
                      yAxisId="gate"
                      stroke="#94A3B8"
                      domain={[0, 1]}
                      ticks={[0, 0.5, 1]}
                      tickFormatter={(v) => (v === 0 ? 'Blocked' : v === 1 ? 'Allowed' : '0.5')}
                    />
                    <YAxis yAxisId="var" orientation="right" stroke="#7dd3fc" domain={['auto', 'auto']} tick={{ fontSize: 10 }} />
                    <Tooltip
                      {...chartTooltipWrapperProps}
                      content={makeChartTooltip((v, name, props) => {
                        const payload = props?.payload || {};
                        if (name === 'Gate') {
                          const reason = payload?.gate_reason;
                          return [
                            reason ? `${v === 1 ? 'Allowed' : 'Blocked'} · ${reason}` : (v === 1 ? 'Allowed' : 'Blocked'),
                            'Gate',
                          ];
                        }
                        const source =
                          payload?.variance_source === 'entries'
                            ? 'EntriesKF P (quote snapshot)'
                            : payload?.variance_source === 'base'
                              ? 'Base KF P_variance'
                              : 'Unknown';
                        const num = v != null ? Number(v) : NaN;
                        const formatted = Number.isFinite(num)
                          ? (Math.abs(num) < 0.0001 && num !== 0 ? num.toExponential(3) : num.toFixed(6))
                          : '—';
                        return [formatted, `P variance (${source})`];
                      }, { chartAnchorRef: riskGateVarianceChartRef })}
                    />
                    <Legend />
                    {showStartLine && (
                      <ReferenceLine
                        x={fixtureStartTimeMs}
                        yAxisId="gate"
                        stroke="#22C55E"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        label={{ value: 'Start', fill: '#22C55E', fontSize: 11, position: 'top' }}
                        ifOverflow="extendDomain"
                      />
                    )}
                    <Line yAxisId="gate" type="stepAfter" dataKey="gate" stroke="#F59E0B" strokeWidth={2} name="Gate" dot={false} connectNulls />
                    <Line yAxisId="var" type="monotone" dataKey="p_variance" stroke="#38BDF8" strokeWidth={2} name="P variance" dot={false} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
                </div>
              ) : (
                <div className="empty-state compact">No gating / variance data. Click Refresh.</div>
              )}
            </div>
          </section>
          </>
          )}

          {activeTab === 'liability' && (
            <div className="rh-market-dashboard">
              <section className="chart-section">
                <div className="card chart-card full-width">
                  {v3DashboardLoading && !v3DashboardData ? (
                    <div className="empty-state compact">Loading liability…</div>
                  ) : v3DashboardError ? (
                    <div className="empty-state compact">Liability unavailable for this market.</div>
                  ) : v3DashboardData ? (
                    <LiabilityTab
                      betType={v3BetType}
                      setBetType={setV3BetType}
                      data={v3DashboardData}
                    />
                  ) : (
                    <div className="empty-state compact">Select a market to view liability.</div>
                  )}
                </div>
              </section>
            </div>
          )}

          {activeTab === 'orderBook' && (
          <>
          <section className="current-quote-row">
            <div className="manual-intervention-ob-row">
              <div className="card quote-card">
                <h3>Gate & actions</h3>
                {selectedMarket && (
                  <ManualGatingPanel marketKey={selectedMarket} quote={currentQuote} />
                )}
              </div>
              <GateBreakdownPanel quote={currentQuote} />
            </div>
          </section>
          <section className="chart-section">
            <div className="card chart-card full-width">
              <OrderBookTerminal
                marketKey={selectedMarket}
                quote={currentQuote}
                takenLine={orderBookLine}
              />
            </div>
          </section>
          <section className="chart-section">
            <div className="card chart-card full-width">
              <h3 style={{ marginTop: 0, marginBottom: '0.75rem' }}>Order book clear log</h3>
              <OrderBookClearLog marketKey={selectedMarket} />
            </div>
          </section>
          </>
          )}

          {activeTab === 'models' && (
            <>
              {!selectedMarket ? (
                <div
                  className="card empty-state"
                  style={{
                    padding: '3rem 2rem',
                    textAlign: 'center',
                    color: 'var(--sleeper-gray, #64748B)',
                  }}
                >
                  <p style={{ margin: 0, fontSize: '1rem' }}>Select a market to view derived model breakdown.</p>
                </div>
              ) : (
                <DerivedModelBreakdownPanel
                  isLoading={derivedModelLoading}
                  error={derivedModelIsError ? derivedModelError : null}
                  detail={derivedModelDetail}
                  booklessDerived={booklessDerived || booklessModelsTab}
                  probChartProps={modelsProbChartProps}
                />
              )}
            </>
          )}

          {activeTab === 'uncertainty' && (
            <section className="current-quote-row">
              <UncertaintyBreakdownPanel quote={currentQuote} />
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default DashboardPage;
